<template>
  <div id="selected-order" class="selected-order" v-if="section === 'POS'">
    <div class="loading" v-if="orderLoading">
      <icon-component iconName="loader" />
    </div>
    <div class="order" v-if="order && !orderLoading">
      <div :class="['order-details', { 'retail-mode': isRetailMode }]">
        <div class="col waiter" @click="openModal('waiterModalRef')">
          <div class="title">Waiter</div>
          <div
            class="col-data payer-data"
            :class="{
              'not-specified': !order.opened_by,
              disabled: isPayerFuncDisabled,
            }"
          >
            <span>{{
              order.serviced_by
                ? order.serviced_by.name
                : order.opened_by?.name || "Not specified yet"
            }}</span>
            <icon-component iconName="dropdown" class="dropdown-icon" />
          </div>
        </div>
        <div
          v-if="!isRetailMode"
          class="col table"
          @click="isDefaultOrder && openModal('newOrderModalRef')"
        >
          <div class="title">Table</div>
          <div
            class="col-data"
            :class="[
              { 'not-specified': !order.table },
              { disabled: isPackagePlanOrder },
            ]"
          >
            <span>{{ order.table ? order.table : "N/A" }}</span>
            <icon-component iconName="dropdown" class="dropdown-icon" />
          </div>
        </div>
        <div v-if="!isRetailMode" class="col seat">
          <div class="title">Seat</div>
          <dropdown
            ref="seat-dd"
            :disabled="isPackagePlanOrder"
            :options="calcSeats"
            :has-input="false"
            :has-preselected-value="true"
            :default="filterBySeatsObj"
            :placeholder="'N/A'"
            @onItemClick="seatFilterSelect"
          >
          </dropdown>
        </div>
        <div
          class="col payers"
          @click="
            openModal('payersModalRef', {
              modalTitleText: 'Set the payer',
              modalSubmitBtnText: 'OK',
            })
          "
        >
          <div class="title">Payer</div>
          <div
            class="col-data"
            :class="{ 'not-specified': !order.payers.length }"
          >
            <span>{{
              order.payers.length
                ? `${
                    order.payers[0].first_name ? order.payers[0].first_name : ""
                  } ${order.payers[0].last_name}`
                : "N/A"
            }}</span>
            <icon-component iconName="dropdown" class="dropdown-icon" />
          </div>
        </div>
        <div class="col payer-cabin">
          <div class="title">Cabin</div>
          <div
            class="col-data disabled"
            :class="{
              'not-specified':
                !order.buyer_profile || !order.buyer_profile.cabin,
            }"
          >
            <span>{{
              order.payers.length
                ? order.payers[0].cabin
                  ? order.payers[0].cabin
                  : "N/A"
                : "N/A"
            }}</span>
          </div>
        </div>
        <div class="col col_more">
          <base-button
            v-if="order.id"
            :isDisabled="order.id === tempID"
            :fromMainPOS="true"
            @onClick="toggleOrderDetails(true)"
            text="MORE..."
            classes="def-btn secondary-action-btn uppercase medium border-2 more_btn"
          />
        </div>
      </div>

      <div class="items-list">
        <table v-if="isDefaultOrder">
          <thead>
            <tr>
              <th>Item</th>
              <th>Price</th>
              <th>QTY</th>
              <th>Disc.</th>
              <th>Tax</th>
              <th>Total</th>
              <th>Seat</th>
              <th>St.</th>
              <th>DEL</th>
            </tr>
          </thead>

          <tbody>
            <template
              v-for="(i, index) in filterBySeats !== 'All'
                ? filterList
                : order.items"
              :key="index"
            >
              <tr>
                <td @click="openItemEditModal(i)">
                  <span>{{ i.name }}</span>
                </td>

                <td>{{ calcItemPrice(i) }}</td>

                <td class="qty-td">
                  <div>
                    <icon-component
                      v-show="i.status === 1"
                      iconName="minusBg"
                      @click.prevent="changeQty(i, 'minus')"
                    />

                    <span>{{ i.count }}</span>

                    <icon-component
                      v-show="i.status === 1"
                      iconName="plusBg"
                      @click.prevent="changeQty(i, 'plus')"
                    />
                  </div>
                </td>

                <td
                  class="item-discount"
                  @click.prevent="
                    openModal('setDiscountGratuityModalRef', {
                      title: 'Item discount',
                      index: index,
                    })
                  "
                >
                  <span>{{ calcItemDiscount(i) }}</span>
                </td>

                <td class="item-tax">
                  {{ Number(i?.tax_amount ?? 0).toFixed(2) }}
                </td>

                <td>{{ calcItemTotal(i) }}</td>

                <td>
                  {{ order.table ? (i.seat ? i.seat : "1") : "N/A" }}
                </td>

                <td>
                  <div
                    class="state"
                    @click="openModal('changeItemStateModalRef', i)"
                  >
                    <div
                      :class="`circle status status-${i.status}`"
                    ></div>
                  </div>
                </td>

                <td class="del">
                  <button
                    v-if="
                      filterBySeats !== 'All'
                        ? filterList.length > 1
                        : order.items.length > 1
                    "
                    @click="deleteItem(index)"
                  >
                    <icon-component iconName="delete" />
                  </button>
                </td>
              </tr>

              <tr
                v-for="itemCondiment in getDisplayedCondiments(
                  i?.condiments
                )"
                :key="itemCondiment.id"
              >
                <td @click="openItemEditModal(i)">
                  <span class="condiment-name">
                    {{
                      itemCondiment
                        ?.menu_item_condiment
                        ?.menu_item
                        ?.name
                    }}
                  </span>
                </td>

                <td>
                  {{ parseFloat(itemCondiment.price)?.toFixed(2) }}
                </td>

                <td class="qty-td">
                  <div>
                    <icon-component
                      v-show="i.status === 1"
                      iconName="minusBg"
                      style="visibility: hidden; opacity: 0"
                    />

                    <span class="condiment-qty">
                      {{ itemCondiment.count }}
                    </span>

                    <icon-component
                      v-show="i.status === 1"
                      iconName="minusBg"
                      style="visibility: hidden; opacity: 0"
                    />
                  </div>
                </td>

                <td class="item-discount"></td>
                <td class="item-tax"></td>

                <td>
                  {{ parseFloat(itemCondiment.total)?.toFixed(2) }}
                </td>

                <td></td>

                <td>
                  <div class="state"></div>
                </td>

                <td class="del"></td>
              </tr>
            </template>
          </tbody>
        </table>

        <table v-if="isPackagePlanOrder" class="package-plan__table">
          <thead>
            <tr>
              <th class="package-plan__th-item">Item</th>
              <th class="package-plan__th-payer">Payer</th>
              <th class="package-plan__th-price">Price</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(orderItem, orderItemIdx) in filterBySeats !== 'All'
                ? filterList
                : order.items"
              :key="orderItemIdx"
            >
              <td class="package-plan__th-item">
                <span>
                  {{ orderItem.name }}
                </span>
              </td>

              <td class="package-plan__th-payer payer">
                {{ orderItem?.user_profile?.first_name }}
                {{ orderItem?.user_profile?.last_name }}
              </td>

              <td class="package-plan__th-price">
                {{ parseFloat(orderItem.price).toFixed(2) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <package-plans
        :order="order"
        :user="order?.payers?.[0] ?? null"
        @selected:change="changeSelectedPackagePlan"
      />

      <div class="order-calculations">
        <div class="calc-wrapper">
          <div class="row">
            <div class="left-column"></div>
            <div class="right-column">
              <div class="title title_first">subtotal:</div>
              <div class="amount amount_first">
                {{
                  order.items.length > 0
                    ? isItemDiscountLevel
                      ? order.original_subtotal?.toFixed(2)
                      : order.subtotal?.toFixed(2)
                    : "0.00"
                }}
              </div>
            </div>
          </div>
          <div v-if="isItemDiscountLevel" class="row">
            <div class="left-column"></div>
            <div class="right-column">
              <div class="title title_first">DISCOUNTS:</div>
              <div class="amount amount_first">
                -{{ totalOrderItemsDiscount }}
              </div>
            </div>
          </div>
          <div v-if="!isItemDiscountLevel" class="row">
            <div class="left-column"></div>
            <div class="right-column">
              <div class="title">discount:</div>
              <div class="amount">{{ calcDiscount }}</div>
            </div>
          </div>
          <div class="row" v-if="order">
            <div class="left-column"></div>
            <div class="right-column">
              <div class="title">tax:</div>
              <div class="amount">{{ taxAmountFormatted }}</div>
            </div>
          </div>
          <div class="row" v-if="!isRetailMode">
            <div class="left-column"></div>
            <div class="right-column">
              <div class="title">Additional Tip:</div>
              <div class="amount autoGratuity">
                {{
                  order.gratuity_amount
                    ? order.gratuity_amount.toFixed(2)
                    : "0.00"
                }}
              </div>
            </div>
          </div>
          <div v-if="!isRetailMode" class="row">
            <div class="left-column"></div>
            <div class="right-column">
              <div class="title">AUTO Gratuity:</div>
              <div class="amount autoGratuity">
                {{
                  order?.auto_gratuity_amount
                    ? order.auto_gratuity_amount.toFixed(2)
                    : "0.00"
                }}
              </div>
            </div>
          </div>

          <div class="row total">
            <div class="left-column"></div>
            <div class="right-column">
              <div class="title">total:</div>
              <div class="amount">
                {{ order.total ? order.total.toFixed(2) : "0.00" }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="controls">
        <div
          v-if="isDefaultOrder"
          :class="['row', isRetailMode ? 'x1' : 'auto']"
        >
          <base-button
            :fromMainPOS="true"
            text="edit discount"
            classes="def-btn secondary-action-btn uppercase medium border-2"
            @onClick="
              openModal('setDiscountGratuityModalRef', { title: 'discounts' })
            "
          />
          <base-button
            v-if="!isRetailMode && canShowTipBtn"
            :fromMainPOS="true"
            :text="addTipTitle"
            classes="def-btn secondary-action-btn uppercase medium border-2"
            @onClick="
              openModal('setDiscountGratuityModalRef', { title: 'gratuity' })
            "
          />
          <base-button
            v-if="!isRetailMode"
            :fromMainPOS="true"
            text="add round"
            classes="def-btn secondary-action-btn uppercase medium border-2"
            @onClick="addRound"
          />
        </div>

        <div :class="['row', 'x1']">
          <base-button
            :fromMainPOS="true"
            v-if="!isRetailMode && isDefaultOrder"
            text="Service Chit"
            classes="def-btn secondary-action-btn uppercase medium border-2 icon left"
            @onClick="printMethodsBL({ type: 'runner-chit' })"
            :img-src="'printFoodRunner'"
          />

          <base-button
            v-if="!isRetailMode && canShowTipBtn && isPackagePlanOrder"
            :fromMainPOS="true"
            :text="addTipTitle"
            classes="def-btn secondary-action-btn uppercase medium border-2 left"
            @onClick="
              openModal('setDiscountGratuityModalRef', { title: 'gratuity' })
            "
          />
        </div>
        <div class="row x2">
          <base-button
            :fromMainPOS="true"
            text="cancel check"
            @onClick="openModal('cancelOrderModalRef')"
            classes="def-btn cancel-action-btn uppercase medium border-2"
            :is-disabled="!isManager"
          />
          <base-button
            :fromMainPOS="true"
            text="Close Check"
            @onClick="openModal('closeOrderModalRef')"
            classes="def-btn main-action-btn uppercase medium border-2 icon left close-check"
            :is-disabled="isWaiterAssist"
            img-src="closeOrderWh"
            custom-styles="background-color: #82B941; border-color: #82B941;"
          />
        </div>
      </div>
    </div>

    <div class="no-order-selected" v-if="!order && !orderLoading">
      No orders selected
    </div>
  </div>

  <teleport to="body">
    <div v-if="showMoreDetails && order" id="check-details-wrapper">
      <CheckDetailsComponent
        :selectedOrder="order"
        :section="section"
        :orderLoading="orderLoading"
        @backToPos="toggleOrderDetails"
        @changeTable="openModal('newOrderModalRef')"
        @changeServing="openModal('waiterModalRef')"
        @editDiscounts="
          openModal('setDiscountGratuityModalRef', { title: 'discounts' })
        "
        @addGratuity="
          openModal('setDiscountGratuityModalRef', { title: 'gratuity' })
        "
        @printCheck="openModal('printCheckModalRef')"
        @cancelOrder="openModal('cancelOrderModalRef')"
        @closeOrder="openModal('closeOrderModalRef')"
        @setItemDiscount="openModal"
        @showDiscountDetails="openModal('discountDetailsModalRef')"
        @deletePayer="deletePayerBl"
        @reopenOrder="openCommentModal"
        @resetOrder="resetOrderBL"
        @cancelResetingOrder="cancelResetingOrder"
        @addPayer="openModal"
        @toggleCPT="toggleCPTBL"
        @houseAccountInputChange="houseAccountInputChange"
        @packagePlan:change="changeSelectedPackagePlan"
      />
    </div>

    <cancel-order-modal
      :selectedOrder="order"
      ref="cancelOrderModalRef"
      v-if="cancelOrderModalRef"
      @onModalClose="onModalClose('cancelOrderModalRef')"
      @cancelOrder="cancelOrderBL"
    />

    <close-order-modal
      :selectedOrder="order"
      ref="closeOrderModalRef"
      v-if="closeOrderModalRef"
      @toggleCPT="toggleCPTBL"
      @onModalClose="onModalClose('closeOrderModalRef')"
      @houseAccountInputChange="houseAccountInputChange"
    />

    <guest-signature-handoff-modal
      v-if="pendingGuestFlowData"
      :order="order"
      :payers="pendingGuestFlowData.payers"
      @cancel="cancelGuestHandoff"
      @confirm="confirmGuestHandoff"
    />

    <guest-signature-flow
      v-if="guestFlowData"
      ref="guestFlowRef"
      :order="guestFlowData.order"
      :payers="guestFlowData.closeData.payers"
      @confirm="signGuestOrder"
      @skip="skipGuestSignature"
      @cancel="cancelGuestSignature"
      @timeout="cancelGuestSignature"
      @done="finishGuestSignature"
    />

    <print-check-modal
      v-if="printCheckModalRef"
      :selectedOrder="order"
      ref="printCheckModalRef"
      @printMethods="printMethodsBL"
      @onModalClose="onModalClose('printCheckModalRef')"
    />

    <new-order-modal-component
      v-if="newOrderModalRef"
      :is-edit="true"
      ref="newOrderModalRef"
      :selected-order="order"
      @changeOrderTable="changeOrderTableBL"
      @onModalClose="onModalClose('newOrderModalRef')"
    />

    <add-edit-item-modal
      v-if="addEditItemModalRef"
      @addEditItem="editItemBL"
      @deleteItem="deleteItemBL"
      :action="'edit'"
      :selectedItem="selectedItem"
      :selectedOrder="order"
      ref="addEditItemModalRef"
      @onModalClose="onModalClose('addEditItemModalRef')"
    />

    <select-the-printer-modal
      v-if="selectThePrinterModalRef"
      ref="selectThePrinterModalRef"
      @printFoodRunner="printMethodsBL"
      @selectedPrinter="selectPrinter"
      @onModalClose="onModalClose('selectThePrinterModalRef')"
    />

    <users-modal
      v-if="payersModalRef"
      ref="payersModalRef"
      :selectedOrder="order"
      :set-waiter="false"
      @addPayer="setPayerBL"
      @onModalClose="onModalClose('payersModalRef')"
    />

    <waiter-modal
      v-if="waiterModalRef"
      ref="waiterModalRef"
      :selectedOrder="order"
      :set-waiter="true"
      @setWaiter="setWaiterBL"
      @onModalClose="onModalClose('waiterModalRef')"
    />

    <change-item-state-modal
      v-if="changeItemStateModalRef"
      ref="changeItemStateModalRef"
      :selectedOrder="order"
      @change-item-status="changeItemStatusBL"
      @onModalClose="onModalClose('changeItemStateModalRef')"
    />

    <discount-details-modal-component
      v-if="discountDetailsModalRef"
      ref="discountDetailsModalRef"
      :selectedOrder="order"
      @onModalClose="onModalClose('discountDetailsModalRef')"
    />

    <confirmation-modal-component
      ref="confirmationModalRef"
      :has-cancel-btn="false"
    />

    <confirmation-modal-component
      ref="upsellConfirmationModalRef"
      @onConfirmationModalConfirm="confirmUpsell"
      @onConfirmationModalClose="finishUpsellPrompt"
    />

    <set-discount-gratuity-modal-component
      v-if="setDiscountGratuityModalRef"
      ref="setDiscountGratuityModalRef"
      :selectedOrder="order"
      @changeItemDiscount="changeItemDiscountBL"
      @changeOrderDiscount="changeOrderDiscountBL"
      @changeGratuity="changeGratuityBL"
      @onModalClose="onModalClose('setDiscountGratuityModalRef')"
    />

    <item-deletion-notice-modal
      v-if="itemDeletionNoticeModalRef"
      ref="itemDeletionNoticeModalRef"
      :selected-order="order"
      :selectedItem="selectedItem"
      @deleteItem="deleteItemBL"
    />

    <comment-modal
      v-if="commentModalRef"
      ref="commentModalRef"
      @submitComment="reopenOrderBL"
      @onModalClose="onModalClose('commentModalRef')"
      :title="'Comment'"
      :sub-title="'Please, add a comment to the reopen order'"
      :sub-title-info="'(required)'"
      :submit-btn-text="'reopen'"
      :requiredComment="true"
    />

    <confirm-modal ref="confirm-modal" />

    <error-modal
      ref="deiscount-error-modal"
      v-if="canShowDiscountErrorModal"
    />

    <price-modal
      v-if="needSetPrice"
      ref="openPriceModalRef"
      @onModalClose="needSetPrice = false"
    />
  </teleport>
</template>

<script>
import CheckDetailsComponent from "@/views/checks/CheckDetails";
import Dropdown from "@/components/_shared/Dropdown";
import ModalComponent from "@/components/_shared/Modal";
import PrintCheckModal from "@/components/_modals/PrintCheckModal";
import NewOrderModalComponent from "@/components/_modals/NewOrderModal";
import CancelCloseOrder from "@/components/_modals/CancelCloseOrder";
import AddEditItemModal from "@/components/_modals/AddEditItemModal";
import SelectThePrinterModal from "@/components/_modals/SelectThePrinterModal";
import FoodRunnerModal from "@/components/_modals/FoodRunnerModal";
import ChangeItemStateModal from "@/components/_modals/ChangeItemStateModal";
import SplitOrderModal from "@/components/_modals/SplitOrderModal";
import DiscountDetailsModalComponent from "@/components/_modals/DiscountDetailsModal";
import UsersModal from "@/components/_modals/UsersModal";
import ConfirmationModalComponent from "@/components/_shared/ConfirmationModal";
import { usersService } from "@/_api/users.api";
import WaiterModal from "@/components/_modals/WaiterModal";
import SetDiscountGratuityModalComponent from "@/components/_modals/SetDiscountGratuityModal";
import ItemDeletionNoticeModal from "@/components/_modals/ItemDeletionNoticeModal";
import { orderService } from "@/_api/order.api";
import { printersService } from "@/_api/printers.api";
import { formatDataService } from "@/_helpers/format-data";
import { constants } from "@/constants/constants";
import BaseButton from "@/components/_shared/Button.vue";
import CancelOrderModal from "@/components/_modals/CancelOrderModal.vue";
import CloseOrderModal from "@/components/_modals/CloseOrderModal.vue";
import GuestSignatureHandoffModal from "@/components/_modals/GuestSignatureHandoffModal.vue";
import GuestSignatureFlow from "@/components/pos/GuestSignatureFlow.vue";
import Switch from "@/components/_shared/Switch.vue";
import store from "@/store";
import iconComponent from "@/components/_shared/Icon.vue";
import CommentModal from "@/components/_modals/CommentModal.vue";
import {
  calculateTotalDiscount,
  calculateItemDiscountInAmount,
  needShowDrinkLimitModal,
  needShowDrinkTimeLimitModal,
  getPayerAlcoholRestrictedDate,
  getDisplayedCondiments,
  getButtonTipTitle,
  calculateTotalItemsDiscount,
  calculateItemLevelDiscount,
  getUpsellTitle,
  hasSelectedUpsellCondiment,
  isTruthyFlag,
  isUpsell,
} from "@/_helpers/order";
import ErrorModal from "../_modals/ErrorModal.vue";
import CustomCheckbox from "@/components/_shared/CustomCheckbox.vue";
import PackagePlans from "@/components/_shared/PackagePlans.vue";
import { init as initSettings } from "@/_helpers/settings";
import { settingsService } from "@/_api/settings.api";
import { packagePlansService } from "@/_api/packagePlans.api";
import ConfirmModal from "../_modals/ConfirmModal.vue";
import * as JSPM from "jsprintmanager";
import {webusbPrinter} from "@/_helpers/webusb-printer";
import PriceModal from "@/components/_modals/PriceModal.vue";

export default {
  name: "SelectedOrderComponent",

  props: {
    tempOrder: {
      require: false,
      type: Object,
    },

    section: {
      required: true,
      type: String,
      default: "POS",
    },
  },

  emits: [
    "addEditItem",
    "selectedPrinter",
    "print",
    "updateOrder",
    "singleOrderLoading",
    "showConfirm:drinkLimit",
  ],

  components: {
    ConfirmModal,
    PackagePlans,
    CustomCheckbox,
    ErrorModal,
    CloseOrderModal,
    GuestSignatureHandoffModal,
    GuestSignatureFlow,
    CancelOrderModal,
    BaseButton,
    UsersModal,
    WaiterModal,
    DiscountDetailsModalComponent,
    SplitOrderModal,
    ChangeItemStateModal,
    FoodRunnerModal,
    SelectThePrinterModal,
    CancelCloseOrder,
    AddEditItemModal,
    ModalComponent,
    PrintCheckModal,
    Dropdown,
    NewOrderModalComponent,
    ConfirmationModalComponent,
    CheckDetailsComponent,
    SetDiscountGratuityModalComponent,
    ItemDeletionNoticeModal,
    Switch,
    iconComponent,
    CommentModal,
    PriceModal,
  },

  data() {
    return {
      order: null,
      orderForReset: null,
      orderResetReasons: [],
      orderForCancelReset: null,
      orderLoading: false,
      showMoreDetails: false,
      needSetPrice: false,
      tempID: "",

      payersModalRef: false,
      waiterModalRef: false,
      selectThePrinterModalRef: false,
      printCheckModalRef: false,
      addEditItemModalRef: false,
      newOrderModalRef: false,
      changeItemStateModalRef: false,
      discountDetailsModalRef: false,
      setDiscountGratuityModalRef: false,
      itemDeletionNoticeModalRef: false,
      closeOrderModalRef: false,
      pendingGuestFlowData: null,
      guestFlowData: null,
      cancelOrderModalRef: false,
      commentModalRef: false,

      selectedPayer: null,
      tax: false,
      autoGratuity: false,
      discount: false,

      stateColor: {
        1: "#EB262B",
        2: "#FFCB05",
        3: "#82B941",
      },

      waitersList: [
        {
          id: 1,
          name: "Pedro gonsalez",
        },
        {
          id: 2,
          name: "Pedro gonsalez 2",
        },
        {
          id: 3,
          name: "Pedro gonsalez 3",
        },
      ],

      payersList: [],
      tablesList: [],
      seatsList: [],
      selectedItem: {},
      selectedPrinter: [],
      filterBySeats: "All",
      canShowDiscountErrorModal: false,
      discountErrorText: "",
      selected_printer: "",
      date: null,
      isQtyChanging: false,
      isPackagePlanUpdating: false,
      isOrderCreating: false,
      pendingUpsell: null,
      upsellAccepted: false,
      packagePlanDetailsCache: {},
    };
  },

  computed: {
    isItemDiscountLevel() {
      return this.order?.department?.discount_level !== 2;
    },

    totalOrderItemsDiscount() {
      return calculateTotalItemsDiscount(this.order);
    },

    canShowTipBtn() {
      if (this.isManager) {
        return true;
      }

      return this.getPosData?.department?.enable_tip_btn;
    },

    addTipTitle() {
      return getButtonTipTitle(this.order);
    },

    getPosData() {
      return this.$store.state.auth.posData;
    },

    isPackagePlanOrder() {
      return this.order.type === "PP";
    },

    isDefaultOrder() {
      return !this.isPackagePlanOrder;
    },

    authUser() {
      return this.$store.state.auth.user;
    },

    isRetailMode() {
      return this.$store.state.auth?.posData?.department?.retail_mode;
    },

    design() {
      return this.$store.state.UI;
    },

    filterBySeatsObj() {
      return {
        name: this.filterBySeats
      };
    },

    calcSeats() {
      let seats = [
        ...new Set(
          this.order.items.map((item) => {
            return item.seat;
          }),
        ),
      ].sort((firstSeat, secondSeat) => firstSeat - secondSeat);

      let options = seats.map((seat) => {
        return {
          name: seat,
        };
      });

      options.unshift({
        name: "All"
      });

      if (this.order.table) {
        return options;
      }

      return [];
    },

    filterList() {
      return this.order.items.filter((item) => {
        return item.seat === this.filterBySeats;
      });
    },

    getUser() {
      return this.$store.state.auth.user;
    },

    isManager() {
      return this.$store.state.auth.user?.user.role === "manager";
    },

    isWaiterAssist() {
      return this.$store.state.auth.user?.user.role === "waiter assist";
    },

    calcDiscount() {
      return calculateTotalDiscount(this.order);
    },

    hasDiscountRow() {
      return Boolean(this.order && this.order.items?.length);
    },

    taxAmountFormatted() {
      return Number(this.order?.tax_amount ?? 0).toFixed(2);
    },

    isPayerFuncDisabled() {
      if (
        this.order
          && this.order.id === constants.tempOrderID
          && this.getUser.user.role !== "manager"
      ) {
        return true;
      }

      if (
        this.getUser
          && this.getUser.user.role === "waiter assist"
      ) {
        return true;
      }

      return false;
    },

    calcTotal() {
      let total = 0;

      this.order.items.forEach((item) => {
        item.condiments_parsed.forEach((condiment) => {
          if (condiment.type !== 1) {
            return;
          }

          condiment.items.forEach((condimentItem) => {
            if (condimentItem.checked) {
              total += (
                condimentItem.count
                  * condiment.count
                  * parseFloat(condimentItem.price).toFixed(2)
              );
            }
          });
        });
      });

      return parseFloat(
        this.order.subtotal + total
      ).toFixed(2);
    },

    calcFullTotal() {
      let total = 0;

      this.order.items.forEach((item) => {
        item.condiments_parsed.forEach((condiment) => {
          if (condiment.type !== 1) {
            return;
          }

          condiment.items.forEach((condimentItem) => {
            if (condimentItem.checked) {
              total += (
                condimentItem.count
                  * condiment.count
                  * parseFloat(condimentItem.price).toFixed(2)
              );
            }
          });
        });
      });

      if (this.order.discount !== null) {
        return parseFloat(
          this.order.total + total
        ).toFixed(2);
      }

      return parseFloat(
        this.order.subtotal + total
      ).toFixed(2);
    },

    getBusinessDay() {
      return this.$store.state.settings.businessDay;
    },

    getCheckPrinter() {
      return this.$store.state.auth.posData.printer;
    },
  },

  created() {},

  mounted() {
    if (store.state.settings.selectedPrinterPM) {
      this.selected_printer =
        store.state.settings.selectedPrinterPM;
    }

    this.tempID = constants.tempOrderID;

    if (this.section === "CHECKS") {
      const id = this.$route?.params?.id;

      if (id) {
        this.getOrder(id);
        this.toggleOrderDetails(true);
      } else {
        console.warn(
          "[SelectedOrder] CHECKS mode: route.params.id is missing"
        );
      }
    }

    this.emmiter.on("addItem", (data) => {
      this.addItemBL(data);
    });

    this.emmiter.on("addPackagePlan", (data) => {
      this.addPackagePlan(data);
    });

    this.emmiter.on("closeOrder", (data) => {
      if (this.shouldStartGuestFlow(data)) {
        this.pendingGuestFlowData = JSON.parse(JSON.stringify(data));
      } else {
        this.closeOrderBL(data);
      }
    });

    this.emmiter.on("closePackagePlanOrder", (order) => {
      this.closePackagePlanOrder(order);
    });

    this.emmiter.on("cancelOrder", (data) => {
      this.cancelOrderBL(data);
    });

    this.emmiter.on("togglePT", (data) => {
      this.toggleCPTBL(data);
    });

    settingsService
      .getSettings()
      .then((res) => {
        initSettings(this.$store, res.data);
      })
      .catch((err) => {
        console.error(err);
      });
  },

  beforeUnmount() {
    this.emmiter.off("addItem");
    this.emmiter.off("addPackagePlan");
    this.emmiter.off("closeOrder");
    this.emmiter.off("cancelOrder");
    this.emmiter.off("togglePT");
    this.emmiter.off("closePackagePlanOrder");
  },

  methods: {
    isValidOrderId(id) {
      return id !== undefined
        && id !== null
        && id !== ""
        && id !== "undefined";
    },

    getOrder(id) {
      if (!this.isValidOrderId(id)) {
        console.warn(
          "[SelectedOrder] getOrder called with invalid id:",
          id
        );

        return;
      }

      this.orderLoading = true;

      this.emmiter.emit(
        "singleOrderLoading",
        this.orderLoading
      );

      orderService
        .getSingleOrder(id)
        .then((res) => {
          this.order = res;

          if (this.isDefaultOrder) {
            this.order.items =
              formatDataService.handleCondimentsModifiers(
                this.order.items
              );
          }

          this.orderForReset = this.order;

          this.orderForCancelReset =
            JSON.parse(
              JSON.stringify(this.order)
            );
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          this.orderLoading = false;

          this.emmiter.emit(
            "singleOrderLoading",
            this.orderLoading
          );
        });
    },

    openItemEditModal(item) {
      if (item.open_price) {
        this.needSetPrice = true;

        this.$nextTick(() => {
          this.$refs["openPriceModalRef"].openModal({
            item: item,

            onConfirm: (price) => {
              item.open_price_value = price;

              this.editItemBL(item);
            },
          });
        });

        return;
      }

      this.openModal(
        "addEditItemModalRef",
        item
      );
    },

    async changeSelectedPackagePlan(
      selectedPackagePlan
    ) {
      this.order.package_plan_user_profile_id =
        selectedPackagePlan;

      try {
        this.order.items =
          calculateItemLevelDiscount(
            this.order
          );

        const { data } =
          await orderService.updateOrder(
            formatDataService.formatItemsForAPI(
              this.order.items
            ),
            this.order,
          );

        this.handleAPIResponse(
          data,
          false,
          true
        );
      } catch (error) {
        store.dispatch(
          "alert/error",
          error
        );
      }
    },

    getDisplayedCondiments,

    getCurrentOrderWithoutRefresh() {
      orderService
        .getSingleOrder(
          this.order.id
        )
        .then((res) => {
          this.order = res;

          this.order.items =
            formatDataService.handleCondimentsModifiers(
              this.order.items,
            );

          this.orderForReset =
            this.order;

          this.orderForCancelReset =
            JSON.parse(
              JSON.stringify(
                this.order
              )
            );
        })
        .catch((err) => {
          console.error(err);
        });
    },

    handleAPIResponse(
      data,
      closeModal,
      updateOrderInList,
      updateOrderInListAction,
      removedItem,
    ) {
      if (closeModal) {
        this.emmiter.emit("closeModal");

        if (this.$refs["closeOrderModalRef"]) {
          this.$refs["closeOrderModalRef"].close();
        }
      }

      if (data) {
        this.order =
          JSON.parse(JSON.stringify(data));

        this.orderForCancelReset =
          JSON.parse(JSON.stringify(data));

        if (this.order.type !== "PP") {
          this.order.items =
            formatDataService.handleCondimentsModifiers(
              this.order.items,
            );
        }

        if (updateOrderInListAction === "replace") {
          this.$emit("updateOrder", {
            ...this.order,
            active: true
          });
        }
      }

      if (updateOrderInListAction === "remove") {
        this.$emit("updateOrder", null);
      }

      if (updateOrderInList) {
        if (updateOrderInListAction !== "remove") {
          this.$emit(
            "updateOrder",
            this.order
          );
        }

        this.emmiter.emit(
          "updateOrderInList",
          {
            data: data,
            action: updateOrderInListAction,
            removedItem: removedItem,
          }
        );
      }
    },

    handleRecalculateAPIResponse(
      data,
      closeModal
    ) {
      if (closeModal) {
        this.emmiter.emit("closeModal");

        if (this.$refs["closeOrderModalRef"]) {
          this.$refs["closeOrderModalRef"].close();
        }
      }

      if (data) {
        this.order =
          JSON.parse(JSON.stringify(data));

        this.order.items =
          formatDataService.handleCondimentsModifiers(
            this.order.items,
          );

        this.$emit("updateOrder", {
          ...this.order,
          active: true
        });
      }
    },

    openModal(type, data) {
      switch (type) {
        case "cancelOrderModalRef":
          if (!this.isManager) {
            return;
          }

          if (!this.order.items.length) {
            this.emmiter.emit(
              "cancelOrder",
              this.order
            );
          } else {
            this.cancelOrderModalRef = true;

            this.$nextTick(() => {
              this.$refs[
                "cancelOrderModalRef"
              ].openModal(this.order);
            });
          }

          break;

        case "closeOrderModalRef":
          if (
            !this.isWaiterAssist
            && this.order.items.length > 0
          ) {
            this.closeOrderModalRef = true;

            this.$nextTick(() => {
              this.$refs[
                "closeOrderModalRef"
              ].openModal();
            });
          }

          break;

        case "setDiscountGratuityModalRef":
          if (data.title === "discounts") {
            const canShowDiscountModal =
              Boolean(
                this.order?.payers?.length > 0
                && this.order.items.length > 0
              );

            if (canShowDiscountModal) {
              this.setDiscountGratuityModalRef = true;

              this.$nextTick(() => {
                this.$refs[
                  "setDiscountGratuityModalRef"
                ].openModal({
                  title: "All discounts",
                });
              });
            }

            break;
          }

          if (data.title === "gratuity") {
            if (this.order.items.length > 0) {
              this.setDiscountGratuityModalRef = true;

              this.$nextTick(() => {
                this.$refs[
                  "setDiscountGratuityModalRef"
                ].openModal({
                  title: "Gratuity",
                });
              });
            }

            break;
          }

          if (data.title === "Item discount") {
            const canShowDiscountModal =
              Boolean(
                this.order?.payers?.length > 0
                && this.order.items.length > 0
              );

            if (canShowDiscountModal) {
              this.setDiscountGratuityModalRef = true;

              this.$nextTick(() => {
                this.$refs[
                  "setDiscountGratuityModalRef"
                ].openModal({
                  title: "Item discount",
                  index: data.index,
                });
              });
            }

            break;
          }

          break;

        case "discountDetailsModalRef":
          if (this.order.items.length > 0) {
            this.discountDetailsModalRef = true;

            this.$nextTick(() => {
              this.$refs[
                "discountDetailsModalRef"
              ].openModal();
            });
          }

          break;

        case "addEditItemModalRef":
          this.selectedItem = data;
          this.addEditItemModalRef = true;

          this.$nextTick(() => {
            this.$refs[
              "addEditItemModalRef"
            ].openModal();
          });

          break;

        case "printCheckModalRef":
          if (this.order.items.length > 0) {
            if (this.getCheckPrinter) {
              this.printCheckModalRef = true;

              this.$nextTick(() => {
                this.$refs[
                  "printCheckModalRef"
                ].openModal(this.order);
              });
            } else {
              this.$refs[
                "confirmationModalRef"
              ].show({
                title: "Printing is not available",
                message:
                  "Please set up the printer first",
                okButton: "OK",
              });
            }
          }

          break;

        case "newOrderModalRef":
          this.newOrderModalRef = true;

          this.$nextTick(() => {
            this.$refs[
              "newOrderModalRef"
            ].openModal(this.order);
          });

          break;

        case "payersModalRef": {
          const isClosedOrderWithPackagePlanItems =
            Boolean(
              2 === this.order.status
              && null !==
                this.order.package_plan_user_profile_id
              && !isNaN(
                this.order.package_plan_user_profile_id
              )
            );

          if (
            this.isPackagePlanOrder
            || isClosedOrderWithPackagePlanItems
          ) {
            this.$refs["confirm-modal"].show({
              title: "Restricted",
              message:
                "Additional payers for the check with a package plan are restricted.",
              buttonLabel: "OK",
            });

            return;
          }

          this.payersModalRef = true;

          this.$nextTick(() => {
            this.$refs[
              "payersModalRef"
            ].openModal(data);
          });

          break;
        }

        case "waiterModalRef":
          if (
            this.getUser.user.role
            !== "waiter assist"
          ) {
            this.waiterModalRef = true;

            this.$nextTick(() => {
              this.$refs[
                "waiterModalRef"
              ].openModal({
                modalTitleText: "Set the waiter",
                modalSubmitBtnText: "CONFIRM",
              });
            });
          } else {
            store.dispatch(
              "alert/error",
              "User does not have access privileges",
            );
          }

          break;

        case "changeItemStateModalRef":
          if (data.status === 1) {
            this.changeItemStateModalRef = true;

            this.$nextTick(() => {
              this.$refs[
                "changeItemStateModalRef"
              ].openModal(data);
            });
          } else if (data.status === 2) {
            this.changeItemStateModalRef = true;

            this.$nextTick(() => {
              this.$refs[
                "changeItemStateModalRef"
              ].openModal(data, true, 0);
            });
          }

          break;

        case "confirmationModalRef":
          if (data === "intoxicated_payer") {
            this.$refs[
              "confirmationModalRef"
            ].show({
              title: "Restricted",
              message:
                "Payers has intoxication state.",
              okButton: "OK",
            });

            break;
          }

          if (data === "add_round_intoxicated") {
            this.$refs[
              "confirmationModalRef"
            ].show({
              title: "Attention",
              message:
                "The round added partially because of intoxicated state of the payer",
              okButton: "OK",
            });

            break;
          }

          if (data === "user_already_added") {
            this.$refs[
              "confirmationModalRef"
            ].show({
              title: "Attention",
              message:
                "User is already in the list",
              okButton: "OK",
            });

            break;
          }

          break;

        case "itemDeletionNoticeModalRef":
          this.itemDeletionNoticeModalRef = true;

          this.$nextTick(() => {
            this.$refs[
              "itemDeletionNoticeModalRef"
            ].openModal();
          });

          break;

        case "commentModalRef":
          this.commentModalRef = true;

          this.$nextTick(() => {
            this.$refs[
              "commentModalRef"
            ].openModal();
          });

          break;
      }
    },

    onModalClose(ref) {
      this[ref] = false;
    },

    toggleOrderDetails(data) {
      this.showMoreDetails = data;

      this.emmiter.emit("toggleModals", {
        opened: this.showMoreDetails,
        type: "SelectedOrderMore",
      });
    },

    calcItemPrice(data) {
      return parseFloat(data.price)?.toFixed(2);
    },

    calcItemDiscount(orderItem) {
      const discount =
        calculateItemDiscountInAmount(
          orderItem
        )?.toString();

      if (discount?.length <= 4) {
        return discount;
      }

      return discount
        ?.toString()
        .substring(0, 4)
        ?.padEnd(6, ".");
    },

    calcItemTotal(data) {
      const total = parseFloat(data.total);

      return total?.toFixed(2);
    },

    async changeQty(item, type) {
      if (this.isQtyChanging) {
        return;
      }

      const alcoholDrinksCount =
        this.order.items.reduce(
          (accumulator, currentItem) => {
            if (
              currentItem.legal_age_class === 1
            ) {
              return accumulator
                + currentItem.count;
            }

            return accumulator;
          },
          0,
        );

      this.isQtyChanging = true;

      if (type === "plus") {
        if (
          item.legal_age_class === 1
          && this.order.payers.length
        ) {
          try {
            const res =
              await usersService.getSinglePosUser(
                this.order.payers[0].id,
              );

            if (res) {
              if (res.intoxication_state) {
                this.openModal(
                  "confirmationModalRef",
                  "intoxicated_payer"
                );

                return;
              }

              const count =
                alcoholDrinksCount + 1;

              if (
                needShowDrinkLimitModal(
                  this.order?.payers,
                  count,
                  this.$store.getters
                    .limitAlcoholPerDay,
                )
              ) {
                this.$emit(
                  "showConfirm:drinkLimit"
                );

                return;
              }

              if (
                needShowDrinkTimeLimitModal(
                  item,
                  this.order
                )
              ) {
                const restrictedUntil =
                  getPayerAlcoholRestrictedDate(
                    this.order
                  );

                this.emmiter.emit(
                  "showTimer",
                  {
                    type: "alcoholDrinkLimit",
                    restrictedUntil,
                  }
                );

                return;
              }

              await this.changeItemQtyBL(
                item,
                type
              );
            }
          } catch (error) {
            console.error(error);
          } finally {
            this.isQtyChanging = false;
          }
        } else {
          await this.changeItemQtyBL(
            item,
            type
          );
        }
      } else {
        await this.changeItemQtyBL(
          item,
          type
        );

        this.isQtyChanging = false;
      }
    },

    selectPrinter(printer) {
      this.selectedPrinter = [];
      this.selectedPrinter.push(printer);
    },

    seatFilterSelect(data) {
      this.filterBySeats = data;
    },

    addRound() {
      if (this.order.payers.length) {
        usersService
          .getSinglePosUser(
            this.order.payers[0].id
          )
          .then((res) => {
            if (res) {
              if (res.intoxication_state) {
                this.openModal(
                  "confirmationModalRef",
                  "add_round_intoxicated"
                );
              }

              this.emmiter.emit(
                "updatePayerData",
                res
              );

              this.order.payers[0] = res;

              this.addRoundBL();
            }
          })
          .catch((err) => {
            console.error(err);
          });
      } else {
        this.addRoundBL();
      }
    },

    deleteItem(index) {
      const dc = JSON.parse(
        JSON.stringify(this.order)
      );

      this.selectedItem = dc.items[index];

      if (this.selectedItem.status !== 1) {
        this.openModal(
          "itemDeletionNoticeModalRef"
        );
      } else {
        this.deleteItemBL({
          type: "update",
          id: this.selectedItem.id,
          orderId: this.order.id,
        });
      }
    },

    houseAccountInputChange(value) {
      this.order[
        "payment_house_account_number"
      ] = value;

      if (this.order.status === 2) {
        this.orderForReset[
          "payment_house_account_number"
        ] = this.order[
          "payment_house_account_number"
        ];
      }
    },

    toggleTaxBL(value) {
      const dc = JSON.parse(
        JSON.stringify(this.order)
      );
    },

    toggleGratuityBL(value) {
      const dc = JSON.parse(
        JSON.stringify(this.order)
      );
    },

    setPayerBL(data, type) {
      if (this.order.status === 2) {
        this.addAdjustReason("Change Payer");

        this.orderForReset["payers"].push(data);

        this.onModalClose("payersModalRef");

        return;
      }

      const dc = JSON.parse(
        JSON.stringify(this.order)
      );

      const payerIndex =
        dc["payers"].findIndex((payer) => {
          return payer.id === data.id;
        });

      if (payerIndex === -1) {
        if (type === "add") {
          dc["payers"].push(data);
        } else {
          dc["payers"][0] = data;
        }

        dc["payment_type"] = 1;

        if (dc.id === constants.tempOrderID) {
          const tempOrders =
            JSON.parse(
              localStorage.getItem("tempOrders")
            ) ?? [];

          const tempOrderIndex =
            tempOrders.findIndex(
              (tempOrder) => {
                return (
                  tempOrder.tempId
                  === dc.tempId
                );
              }
            );

          tempOrders.forEach((tempOrder) => {
            tempOrder.active = false;
          });

          if (tempOrderIndex !== -1) {
            tempOrders[tempOrderIndex] = dc;

            localStorage.setItem(
              "tempOrders",
              JSON.stringify(tempOrders),
            );

            this.handleAPIResponse(
              dc,
              true,
              true
            );

            this.$emit("updateOrder", dc);
          }
        } else {
          orderService
            .updateOrder(
              formatDataService
                .formatItemsForAPI(dc.items),
              dc,
            )
            .then((res) => {
              if (res) {
                this.handleAPIResponse(
                  res.data,
                  true,
                  true
                );

                this.$emit(
                  "updateOrder",
                  res.data
                );
              }
            })
            .catch((err) => {
              store.dispatch(
                "alert/error",
                err.message
              );

              console.error(err);
            });
        }
      } else {
        this.openModal(
          "confirmationModalRef",
          "user_already_added"
        );
      }
    },

    deletePayerBl(index) {
      if (this.order.status === 2) {
        this.addAdjustReason("Change Payer");

        this.orderForReset[
          "payers"
        ].splice(index, 1);

        this.onModalClose("payersModalRef");

        return;
      }

      const dc = JSON.parse(
        JSON.stringify(this.order)
      );

      dc.payers.splice(index, 1);

      if (dc.id === constants.tempOrderID) {
        const tempOrders =
          JSON.parse(
            localStorage.getItem("tempOrders")
          ) ?? [];

        const tempOrderIndex =
          tempOrders.findIndex(
            (tempOrder) => {
              return (
                tempOrder.tempId
                === dc.tempId
              );
            }
          );

        tempOrders.forEach((tempOrder) => {
          tempOrder.active = false;
        });

        if (tempOrderIndex !== -1) {
          tempOrders[tempOrderIndex] = dc;

          localStorage.setItem(
            "tempOrders",
            JSON.stringify(tempOrders),
          );

          this.handleAPIResponse(
            dc,
            true,
            true
          );
        }
      } else {
        orderService
          .updateOrder(
            formatDataService
              .formatItemsForAPI(dc.items),
            dc,
          )
          .then((res) => {
            if (res) {
              if (!res.data.payers.length) {
                res.data.payment_type = 0;
              }

              this.handleAPIResponse(
                res.data,
                true,
                true
              );
            }
          })
          .catch((err) => {
            store.dispatch(
              "alert/error",
              err.message
            );

            console.error(err);
          });
      }
    },

    toggleCPTBL(data) {
      this.order = {
        ...this.order,
        payment_type: data
      };

      if (
        !this.order
          .payment_house_account_number
      ) {
        this.order[
          "payment_house_account_number"
        ] = "";
      }

      if (this.order.status === 2) {
        this.orderForReset = this.order;
      }
    },

    setWaiterBL(data) {
      const dc = JSON.parse(
        JSON.stringify(this.order)
      );

      dc["serviced_by"] = data;

      if (dc.id === constants.tempOrderID) {
        const tempOrders =
          JSON.parse(
            localStorage.getItem("tempOrders")
          ) ?? [];

        const tempOrderIndex =
          tempOrders.findIndex(
            (tempOrder) => {
              return (
                tempOrder.tempId
                === dc.tempId
              );
            }
          );

        if (tempOrderIndex !== -1) {
          tempOrders[tempOrderIndex] = dc;

          localStorage.setItem(
            "tempOrders",
            JSON.stringify(tempOrders),
          );

          this.handleAPIResponse(
            dc,
            true,
            true
          );
        }
      } else {
        if (this.isDefaultOrder) {
          orderService
            .updateOrder(
              formatDataService
                .formatItemsForAPI(dc.items),
              dc,
            )
            .then((res) => {
              if (!res) {
                return;
              }

              if (
                this.getUser.user.role
                  !== "manager"
                && this.getUser.user.id
                  !== data.id
              ) {
                this.handleAPIResponse(
                  res.data,
                  true,
                  true,
                  "remove",
                );
              } else {
                this.handleAPIResponse(
                  res.data,
                  true,
                  true
                );
              }
            })
            .catch((err) => {
              store.dispatch(
                "alert/error",
                err.message
              );

              console.error(err);
            });
        }

        if (this.isPackagePlanOrder) {
          const packagePlans = [];

          dc?.items?.forEach((item) => {
            if (
              !packagePlans.includes(
                item.package_plan_id
              )
            ) {
              packagePlans.push(
                item.package_plan_id
              );
            }
          });

          const payload = {
            crew_id: dc["serviced_by"].id,
            package_plans: packagePlans,

            payers: dc?.payers?.map(
              (payer) => {
                return {
                  user_profile_id: payer.id,
                  addition_mode_id:
                    payer.addition_mode?.id,
                };
              }
            ),
          };

          packagePlansService
            .updatePackagePlanOrder(
              payload,
              dc.id
            )
            .then((res) => {
              this.handleAPIResponse(
                res.data.payload.content,
                true,
                true,
              );
            });
        }
      }
    },

    cancelOrderBL(data) {
      if (!this.isManager) {
        return;
      }

      if (data.id === constants.tempOrderID) {
        const tempOrders =
          JSON.parse(
            localStorage.getItem("tempOrders")
          ) ?? [];

        const tempOrderIndex =
          tempOrders.findIndex(
            (tempOrder) => {
              return (
                tempOrder.tempId
                === this.order.tempId
              );
            }
          );

        if (tempOrderIndex !== -1) {
          tempOrders.splice(
            tempOrderIndex,
            1
          );

          localStorage.setItem(
            "tempOrders",
            JSON.stringify(tempOrders),
          );

          this.handleAPIResponse(
            data,
            true,
            true,
            "remove",
          );
        }

        return;
      }

      if (data.type === "PP") {
        orderService
          .cancelPackagePlanOrder(data)
          .then((res) => {
            if (res) {
              this.handleAPIResponse(
                data,
                true,
                true,
                "remove",
              );

              if (this.section === "CHECKS") {
                this.refreshOrderDataIntoChecksView();
              }
            }
          })
          .catch((err) => {
            console.error(err);
          });

        return;
      }

      orderService
        .cancelOrder(data)
        .then((res) => {
          if (res) {
            this.handleAPIResponse(
              data,
              true,
              true,
              "remove",
            );

            if (this.section === "CHECKS") {
              this.refreshOrderDataIntoChecksView();
            }
          }
        })
        .catch((err) => {
          console.error(err);
        });
    },

    async changeItemQtyBL(item, type) {
      const dc = JSON.parse(
        JSON.stringify(this.order)
      );

      const index = dc.items.findIndex(
        (currentItem) => {
          return currentItem.id === item.id;
        }
      );

      if (index === -1) {
        this.isQtyChanging = false;
        return;
      }

      if (type === "minus") {
        if (dc.items[index].count > 1) {
          dc.items[index].count -= 1;
        }
      } else {
        dc.items[index].count += 1;
      }

      try {
        dc.items =
          calculateItemLevelDiscount(dc);

        const { data } =
          await orderService.updateOrder(
            formatDataService
              .formatItemsForAPI(dc.items),
            dc,
          );

        this.handleAPIResponse(
          data,
          false,
          true
        );
      } catch (err) {
        store.dispatch(
          "alert/error",
          err.message
        );

        console.error(err);
      } finally {
        setTimeout(() => {
          this.isQtyChanging = false;
        }, 20);
      }
    },

    editItemBL(item) {
      const itemIndex =
        this.order.items.findIndex(
          (orderItem) => {
            return orderItem.id === item.id;
          }
        );

      if (itemIndex === -1) {
        return;
      }

      const dc = JSON.parse(
        JSON.stringify(this.order)
      );

      dc.items[itemIndex] = item;

      const optionalCondiments =
        item.condiments_parsed.filter(
          (condiment) => {
            return (
              2 === Number(condiment.type)
              && condiment.checked
              && !hasSelectedUpsellCondiment(
                condiment
              )
            );
          }
        );

      optionalCondiments.forEach(
        (condiment) => {
          const existingItemIndex =
            dc.items.findIndex((orderItem) => {
              return (
                orderItem.orderable_id
                  === condiment.menu_item.id
                && orderItem.orderable_type
                  === condiment
                    .menu_item
                    .orderable_type
                && orderItem.seat === item.seat
                && 1 === Number(
                  orderItem.status
                )
              );
            });

          if (existingItemIndex !== -1) {
            dc.items[
              existingItemIndex
            ].count += condiment.count;
          } else {
            dc.items.push({
              orderable_id:
                condiment.menu_item.id,
              orderable_type:
                condiment.menu_item
                  .orderable_type,
              from_menu: true,
              seat: item.seat,
              status: 1,
              count: condiment.count,
              condiments_parsed: [],
              modifiers_parsed: [],
            });
          }

          condiment.checked = false;
        }
      );

      dc.items =
        calculateItemLevelDiscount(dc);

      orderService
        .updateOrder(
          formatDataService
            .formatItemsForAPI(dc.items),
          dc,
        )
        .then((res) => {
          this.handleAPIResponse(
            res.data,
            true,
            true
          );
        })
        .catch((err) => {
          store.dispatch(
            "alert/error",
            err.message
          );

          console.error(err);
        });
    },

    checkArraysEqual(
      firstArray,
      secondArray
    ) {
      if (firstArray === secondArray) {
        return true;
      }

      if (
        firstArray == null
        || secondArray == null
      ) {
        return false;
      }

      if (
        firstArray.length
        !== secondArray.length
      ) {
        return false;
      }

      for (
        let index = 0;
        index < firstArray.length;
        index += 1
      ) {
        if (
          firstArray[index]
          !== secondArray[index]
        ) {
          return false;
        }
      }

      return true;
    },

    getUpsellCondiment(
      item,
      condimentId = null
    ) {
      for (
        const condiment
        of item?.condiments_parsed ?? []
      ) {
        const condimentItem =
          condiment?.items?.find(
            (currentItem) => {
              const isRequestedCondiment =
                null === condimentId
                || String(
                  currentItem
                    .menu_item_condiment_id
                ) === String(
                  condimentId
                );

              return (
                isRequestedCondiment
                && isUpsell(currentItem)
              );
            }
          );

        const promptText =
          getUpsellTitle(
            condimentItem
          )?.trim();

        if (
          condimentItem
          && promptText
          && 2 === Number(
            condiment.type
          )
        ) {
          return {
            id:
              condimentItem
                .menu_item_condiment_id,
            promptText,
            condiment,
            condimentItem,
          };
        }
      }

      return null;
    },

    getPackagePlanContent(response) {
      return response
        ?.data
        ?.payload
        ?.content
        ?? response
          ?.payload
          ?.content
        ?? response
        ?? null;
    },

    getPackagePlanItems(packagePlan) {
      const plan =
        packagePlan?.package_plan
        ?? packagePlan;

      const directItems =
        plan?.items
        ?? plan?.menu_items
        ?? [];

      const ruleItems =
        (plan?.rules ?? []).reduce(
          (items, rule) => {
            return [
              ...items,
              ...(rule?.items ?? []),
            ];
          },
          [],
        );

      return [
        ...directItems,
        ...ruleItems,
      ];
    },

    async getPackagePlanDetails(
      selectedPackagePlan
    ) {
      const selectedPlan =
        selectedPackagePlan
          ?.package_plan
        ?? selectedPackagePlan;

      const selectedPlanId =
        selectedPackagePlan
          ?.package_plan_id
        ?? selectedPlan?.id;

      if (
        selectedPlanId === null
        || selectedPlanId === undefined
      ) {
        return selectedPackagePlan;
      }

      const cacheKey =
        String(selectedPlanId);

      const cachedPackagePlan =
        this.packagePlanDetailsCache[
          cacheKey
        ];

      if (cachedPackagePlan) {
        return cachedPackagePlan;
      }

      const categoriesResponse =
        await packagePlansService
          .getCategories({
            page: 1,
            per_page: 1000,
          });

      const categoriesContent =
        this.getPackagePlanContent(
          categoriesResponse
        );

      const categories =
        Array.isArray(categoriesContent)
          ? categoriesContent
          : [];

      for (const category of categories) {
        const plansResponse =
          await packagePlansService
            .getPackagePlans(
              category.id,
              {
                page: 1,
                per_page: 1000,
              },
            );

        const plansContent =
          this.getPackagePlanContent(
            plansResponse
          );

        const plans =
          Array.isArray(plansContent)
            ? plansContent
            : (
              Array.isArray(
                plansContent?.list
              )
                ? plansContent.list
                : []
            );

        const packagePlan =
          plans.find((plan) => {
            return String(
              plan?.id
            ) === String(
              selectedPlanId
            );
          });

        if (!packagePlan) {
          continue;
        }

        const packagePlanItems =
          this.getPackagePlanItems(
            packagePlan
          );

        if (packagePlanItems.length) {
          this.packagePlanDetailsCache[
            cacheKey
          ] = packagePlan;

          return packagePlan;
        }

        const detailsResponse =
          await packagePlansService
            .getPackagePlan(
              category.id,
              packagePlan.id,
            );

        const packagePlanDetails =
          this.getPackagePlanContent(
            detailsResponse
          ) ?? packagePlan;

        this.packagePlanDetailsCache[
          cacheKey
        ] = packagePlanDetails;

        return packagePlanDetails;
      }

      return selectedPackagePlan;
    },

    async getSelectedPackagePlan() {
      const packagePlanUserProfileId =
        this.order
          ?.package_plan_user_profile_id;

      const hasSelectedPackagePlan =
        packagePlanUserProfileId !== null
        && packagePlanUserProfileId !== undefined
        && String(
          packagePlanUserProfileId
        ).trim() !== "";

      if (
        !hasSelectedPackagePlan
        || !this.order?.id
      ) {
        return null;
      }

      try {
        const response =
          await orderService
            .getOrderAvailablePackagePlans(
              this.order.id
            );

        const content =
          this.getPackagePlanContent(
            response
          );

        const packagePlans =
          Array.isArray(content)
            ? content
            : [];

        const selectedPackagePlan =
          packagePlans.find((plan) => {
            return String(
              plan
                .package_plan_user_profile_id
            ) === String(
              packagePlanUserProfileId
            );
          });

        if (!selectedPackagePlan) {
          return null;
        }

        const selectedPackagePlanItems =
          this.getPackagePlanItems(
            selectedPackagePlan
          );

        if (selectedPackagePlanItems.length) {
          return selectedPackagePlan;
        }

        return await this
          .getPackagePlanDetails(
            selectedPackagePlan
          );
      } catch (error) {
        console.error(
          "[SelectedOrder] Unable to load selected package plan:",
          error,
        );

        return null;
      }
    },

    async isUpsellIncludedInPackagePlan(
      sourceUpsellCondiment
    ) {
      const upsellMenuItemId =
        sourceUpsellCondiment
          ?.condimentItem
          ?.menu_item_id
        ?? sourceUpsellCondiment
          ?.condimentItem
          ?.menu_item
          ?.id
        ?? sourceUpsellCondiment
          ?.condiment
          ?.menu_item
          ?.id;

      if (
        upsellMenuItemId === null
        || upsellMenuItemId === undefined
      ) {
        return false;
      }

      const selectedPackagePlan =
        await this.getSelectedPackagePlan();

      if (!selectedPackagePlan) {
        return false;
      }

      const packagePlanItems =
        this.getPackagePlanItems(
          selectedPackagePlan
        );

      return packagePlanItems.some(
        (item) => {
          const itemId =
            item?.menu_item?.id
            ?? item?.id;

          return String(itemId)
            === String(
              upsellMenuItemId
            );
        }
      );
    },

    clearUpsellSelection(
      upsellCondiment
    ) {
      if (!upsellCondiment) {
        return;
      }

      upsellCondiment
        .condiment
        .count = 1;

      upsellCondiment
        .condiment
        .items
        ?.forEach((condimentItem) => {
          if (
            isUpsell(
              condimentItem
            )
          ) {
            condimentItem.checked =
              false;

            condimentItem.count = 1;
          }
        });

      upsellCondiment
        .condiment
        .checked =
          upsellCondiment
            .condiment
            .items
            ?.some((condimentItem) => {
              return condimentItem.checked;
            }) ?? false;
    },

    findAddedOrderItem(
      sourceItem,
      existingOrderItemIds
    ) {
      const matchingItems =
        this.order?.items?.filter(
          (orderItem) => {
            const isSameItem =
              String(
                orderItem.orderable_id
              ) === String(
                sourceItem.orderable_id
              );

            const isSameType =
              orderItem.orderable_type
              === sourceItem.orderable_type;

            const isActive =
              1 === Number(
                orderItem.status
              );

            return (
              isSameItem
              && isSameType
              && isActive
            );
          }
        ) ?? [];

      const addedItem =
        matchingItems.find(
          (orderItem) => {
            return (
              orderItem.id != null
              && !existingOrderItemIds.has(
                String(
                  orderItem.id
                )
              )
            );
          }
        );

      return (
        addedItem
        ?? matchingItems[
          matchingItems.length - 1
        ]
        ?? null
      );
    },

    async showUpsellAfterItemAdded(
      sourceItem,
      sourceUpsellCondiment,
      existingOrderItemIds,
    ) {
      if (!sourceUpsellCondiment) {
        return;
      }

      const addedOrderItem =
        this.findAddedOrderItem(
          sourceItem,
          existingOrderItemIds,
        );

      if (
        !addedOrderItem
        || isTruthyFlag(
          addedOrderItem.upsell_showed
        )
      ) {
        return;
      }

      const isIncludedInPackagePlan =
        await this
          .isUpsellIncludedInPackagePlan(
            sourceUpsellCondiment
          );

      if (isIncludedInPackagePlan) {
        return;
      }

      this.pendingUpsell = {
        orderItemId:
          addedOrderItem.id,

        condimentId:
          sourceUpsellCondiment.id,

        condiment: JSON.parse(
          JSON.stringify(
            sourceUpsellCondiment
              .condiment
          ),
        ),
      };

      this.upsellAccepted = false;

      this.$nextTick(() => {
        this.$refs
          .upsellConfirmationModalRef
          ?.show({
            message:
              sourceUpsellCondiment
                .promptText,

            okButton: "YES",
            cancelButton: "NO",
          });
      });
    },

    confirmUpsell() {
      this.upsellAccepted = true;
    },

    async finishUpsellPrompt() {
      if (!this.pendingUpsell) {
        return;
      }

      const pendingUpsell =
        this.pendingUpsell;

      const upsellAccepted =
        this.upsellAccepted;

      this.pendingUpsell = null;
      this.upsellAccepted = false;

      const order = JSON.parse(
        JSON.stringify(this.order)
      );

      const orderItem =
        order.items.find((item) => {
          return (
            String(item.id)
            === String(
              pendingUpsell.orderItemId
            )
          );
        });

      if (!orderItem) {
        return;
      }

      orderItem.upsell_showed = true;

      if (upsellAccepted) {
        let upsellCondiment =
          this.getUpsellCondiment(
            orderItem,
            pendingUpsell.condimentId,
          );

        if (!upsellCondiment) {
          const condiment = JSON.parse(
            JSON.stringify(
              pendingUpsell.condiment
            ),
          );

          orderItem.condiments_parsed =
            orderItem.condiments_parsed ?? [];

          orderItem
            .condiments_parsed
            .push(condiment);

          upsellCondiment =
            this.getUpsellCondiment(
              orderItem,
              pendingUpsell.condimentId,
            );
        }

        if (upsellCondiment) {
          this.clearUpsellSelection(
            upsellCondiment
          );

          upsellCondiment
            .condiment
            .checked = true;

          upsellCondiment
            .condiment
            .items
            .forEach((condimentItem) => {
              const isSelected =
                String(
                  condimentItem
                    .menu_item_condiment_id
                ) === String(
                  pendingUpsell.condimentId
                );

              if (isUpsell(condimentItem)) {
                condimentItem.checked =
                  isSelected;

                condimentItem.count = 1;
              }
            });
        }
      }

      try {
        order.items =
          calculateItemLevelDiscount(order);

        const { data } =
          await orderService.updateOrder(
            formatDataService
              .formatItemsForAPI(
                order.items
              ),
            order,
          );

        this.handleAPIResponse(
          data,
          false,
          true
        );
      } catch (err) {
        store.dispatch(
          "alert/error",
          err.message
        );

        console.error(err);
      }
    },

    addItemBL(el) {
      if (this.isOrderCreating) {
        return;
      }

      this.isOrderCreating = true;

      const existingOrderItemIds =
        new Set(
          this.order?.items
            ?.filter((orderItem) => {
              return null != orderItem.id;
            })
            ?.map((orderItem) => {
              return String(orderItem.id);
            }) ?? [],
        );

      const sourceUpsellCondiment =
        this.getUpsellCondiment(el);

      const needPrepareUpsell =
        Boolean(sourceUpsellCondiment);

      if (sourceUpsellCondiment) {
        this.clearUpsellSelection(
          sourceUpsellCondiment
        );
      }

      const dc = JSON.parse(
        JSON.stringify(this.order)
      );

      const index = dc.items.findIndex(
        (orderItem) => {
          const isSameId =
            orderItem.orderable_id
            === el.orderable_id;

          const isSameType =
            orderItem.orderable_type
            === el.orderable_type;

          const isSameSeat =
            orderItem.seat === el.seat;

          const isAllowStatus =
            1 === Number(orderItem.status);

          const isOpenPrice =
            Boolean(orderItem.open_price);

          if (isOpenPrice) {
            return false;
          }

          return (
            isSameId
            && isSameType
            && isSameSeat
            && isAllowStatus
          );
        }
      );

      const indexWithPostpone =
        dc.items.findIndex((orderItem) => {
          return (
            orderItem.orderable_id
              === el.orderable_id
            && orderItem.orderable_type
              === el.orderable_type
            && orderItem.print_in_min
              === el.print_in_min
            && orderItem.seat === el.seat
            && 1 === Number(
              orderItem.status
            )
          );
        });

      const requiredCondiments =
        el.condiments_parsed.filter(
          (condiment) => {
            return (
              1 === Number(condiment.type)
            );
          }
        );

      const optionalCondiments =
        el.condiments_parsed.filter(
          (condiment) => {
            return (
              2 === Number(condiment.type)
              && condiment.checked
              && !hasSelectedUpsellCondiment(
                condiment
              )
            );
          }
        );

      const isItemDiscountLevel =
        dc.department?.discount_level !== 2;

      if (isItemDiscountLevel) {
        const orderDiscountValue =
          dc.items_discount_value;

        const isItemWithDiscount =
          parseFloat(
            el.m_discount_amount
          ) > 0
          || parseFloat(
            el.m_discount_percentage
          ) > 0;

        if (!isItemWithDiscount) {
          el.m_discount_type =
            dc.items_discount_type;

          if (
            dc.items_discount_type === 1
          ) {
            el.m_discount_amount =
              orderDiscountValue;
          } else {
            el.m_discount_percentage =
              orderDiscountValue;
          }
        }
      }

      if (index !== -1) {
        const existingItemHasUpsell =
          dc.items[index]
            ?.condiments_parsed
            ?.some(
              hasSelectedUpsellCondiment
            );

        const needCreateSeparatePosition =
          Boolean(
            needPrepareUpsell
            || existingItemHasUpsell
            || requiredCondiments.length
            || el.modifiers_parsed.length
          );

        if (needCreateSeparatePosition) {
          el.remove_id = true;
          dc.items.push(el);
        } else if (!el.print_in_min) {
          dc.items[index].count += el.count;

          dc.items[index].upsell_showed =
            Boolean(
              dc.items[index]
                .upsell_showed
              || el.upsell_showed
            );
        } else if (
          indexWithPostpone !== -1
        ) {
          dc.items[
            indexWithPostpone
          ].count += el.count;

          dc.items[
            indexWithPostpone
          ].upsell_showed = Boolean(
            dc.items[
              indexWithPostpone
            ].upsell_showed
            || el.upsell_showed
          );
        } else {
          el.remove_id = true;
          dc.items.push(el);
        }
      } else {
        el.remove_id = true;
        dc.items.push(el);
      }

      optionalCondiments.forEach(
        (condiment) => {
          const existingCondimentIndex =
            dc.items.findIndex(
              (orderItem) => {
                const isCurrentItem =
                  orderItem.orderable_id
                    === condiment.menu_item.id
                  && orderItem.orderable_type
                    === condiment
                      .menu_item
                      .orderable_type
                  && 1 === Number(
                    orderItem.status
                  );

                const selectedModifiers =
                  condiment
                    ?.modifiers
                    ?.filter((modifier) => {
                      return Boolean(
                        modifier.checked
                      );
                    });

                if (selectedModifiers) {
                  const selectedOptions = [];

                  selectedModifiers.forEach(
                    (selectedModifier) => {
                      const options =
                        selectedModifier
                          ?.options
                          ?.filter((option) => {
                            return Boolean(
                              option.checked
                            );
                          });

                      options?.forEach(
                        (option) => {
                          selectedOptions.push(
                            option.id
                          );
                        }
                      );
                    }
                  );

                  const currentOptions = [];

                  orderItem
                    ?.modifiers
                    ?.forEach((modifier) => {
                      modifier
                        ?.options
                        ?.forEach((option) => {
                          currentOptions.push(
                            option.id
                          );
                        });
                    });

                  return (
                    isCurrentItem
                    && this.checkArraysEqual(
                      selectedOptions,
                      currentOptions,
                    )
                  );
                }

                return isCurrentItem;
              }
            );

          if (
            existingCondimentIndex !== -1
          ) {
            dc.items[
              existingCondimentIndex
            ].count += condiment.count;
          } else {
            const orderModifiers =
              JSON.parse(
                JSON.stringify(
                  condiment?.modifiers ?? []
                )
              );

            const modifiers = [];

            orderModifiers.forEach(
              (modifier) => {
                if (!modifier.checked) {
                  return;
                }

                modifier.options =
                  modifier
                    ?.options
                    ?.filter((option) => {
                      return Boolean(
                        option.checked
                      );
                    });

                modifiers.push(modifier);
              }
            );

            dc.items.push({
              orderable_id:
                condiment.menu_item.id,
              orderable_type:
                condiment
                  .menu_item
                  .orderable_type,
              from_menu: true,
              seat: el.seat,
              status: 1,
              count: condiment.count,
              condiments_parsed: [],
              modifiers_parsed: modifiers,
            });
          }

          condiment.checked = false;
        }
      );

      dc.items =
        calculateItemLevelDiscount(dc);

      const showUpsell = () => {
        this.showUpsellAfterItemAdded(
          el,
          sourceUpsellCondiment,
          existingOrderItemIds,
        );
      };

      if (dc.id === constants.tempOrderID) {
        orderService
          .createNewOrder(
            dc.id,
            formatDataService
              .formatItemsForAPI(
                dc.items,
                true
              ),
            dc,
          )
          .then((res) => {
            if (!res) {
              return;
            }

            const tempOrders =
              JSON.parse(
                localStorage.getItem(
                  "tempOrders"
                )
              ) ?? [];

            const tempOrderIndex =
              tempOrders.findIndex(
                (tempOrder) => {
                  return (
                    tempOrder.tempId
                    === dc.tempId
                  );
                }
              );

            if (tempOrderIndex === -1) {
              return;
            }

            const removedItem =
              tempOrders.splice(
                tempOrderIndex,
                1
              );

            localStorage.setItem(
              "tempOrders",
              JSON.stringify(tempOrders),
            );

            this.handleAPIResponse(
              res.data,
              false,
              true,
              "replace",
              removedItem,
            );

            showUpsell();
          })
          .catch((err) => {
            store.dispatch(
              "alert/error",
              err.message
            );

            console.error(err);
          })
          .finally(() => {
            this.isOrderCreating = false;
          });

        return;
      }

      orderService
        .updateOrder(
          formatDataService
            .formatItemsForAPI(dc.items),
          dc,
        )
        .then((res) => {
          this.handleAPIResponse(
            res.data,
            false,
            true,
          );

          showUpsell();
        })
        .catch((err) => {
          store.dispatch(
            "alert/error",
            err.message
          );

          console.error(err);
        })
        .finally(() => {
          this.isOrderCreating = false;
        });
    },

    showNeedSellAllInCabinConfirmModal(packagePlan) {
      const currentComponent = this;

      this.$refs["confirm-modal"]?.show({
        title: "The Guest(s) has other member(s) in the cabin",
        message:
          "According to the rules of the system, each of them must also have this package plan. Continue the purchase and add a package plan for each of the guests?",
        isConfirm: true,
        confirmButton: "YES",
        declineButton: "NO",

        onConfirm: () => {
          currentComponent.createPackagePlanOrder(
            packagePlan
          );

          currentComponent.$refs[
            "confirm-modal"
          ]?.close();
        },
      });
    },

    addPackagePlan(packagePlan) {
      if (packagePlan.needSellEveryInCabin) {
        this.showNeedSellAllInCabinConfirmModal(
          packagePlan
        );

        return;
      }

      this.createPackagePlanOrder(packagePlan);
    },

    async createPackagePlanOrder(packagePlan) {
      if (this.isPackagePlanUpdating) {
        return;
      }

      this.isPackagePlanUpdating = true;

      try {
        const needCreate =
          packagePlan.currentOrder.id
          === constants.tempOrderID;

        let newOrder = null;

        if (needCreate) {
          const { data } =
            await packagePlansService
              .buyPackagePlan(
                packagePlan.payload,
              );

          newOrder = data.payload.content;
        } else {
          const { data } =
            await packagePlansService
              .updatePackagePlanOrder(
                packagePlan.payload,
                packagePlan.currentOrder.id,
              );

          newOrder = data.payload.content;
        }

        if (
          packagePlan.currentOrder.id
          === constants.tempOrderID
        ) {
          const tempOrders =
            JSON.parse(
              localStorage.getItem(
                "tempOrders"
              )
            ) ?? [];

          const tempOrderIndex =
            tempOrders.findIndex(
              (tempOrder) => {
                return (
                  tempOrder.tempId
                  === packagePlan
                    .currentOrder
                    .tempId
                );
              }
            );

          if (tempOrderIndex !== -1) {
            tempOrders.splice(
              tempOrderIndex,
              1
            );

            localStorage.setItem(
              "tempOrders",
              JSON.stringify(tempOrders)
            );
          }
        }

        this.order =
          JSON.parse(
            JSON.stringify(newOrder)
          );

        this.orderForCancelReset =
          JSON.parse(
            JSON.stringify(newOrder)
          );

        this.emmiter.emit(
          "updatePackagePlanOrderInList",
          {
            newOrder: newOrder,
            oldOrder:
              packagePlan.currentOrder,
          }
        );
      } catch (error) {
        console.error(error);
      } finally {
        this.isPackagePlanUpdating = false;
      }
    },

    deleteItemBL(data) {
      const dc = JSON.parse(
        JSON.stringify(this.order)
      );

      if (data.type === "delete") {
        orderService
          .deleteItem(
            {
              count: data.number,
              reason: data.text
            },
            data.id,
            data.orderId,
          )
          .then((res) => {
            this.handleAPIResponse(
              res,
              true,
              true
            );
          })
          .catch((err) => {
            console.error(err);
          });

        return;
      }

      const itemIndex =
        dc.items.findIndex((item) => {
          return item.id === data.id;
        });

      if (itemIndex === -1) {
        return;
      }

      dc.items.splice(itemIndex, 1);

      dc.items =
        calculateItemLevelDiscount(dc);

      orderService
        .updateOrder(
          formatDataService
            .formatItemsForAPI(dc.items),
          dc,
        )
        .then((res) => {
          this.handleAPIResponse(
            res.data,
            true,
            true
          );
        })
        .catch((err) => {
          store.dispatch(
            "alert/error",
            err.message
          );

          console.error(err);
        });
    },

    addRoundBL() {
      const dc = JSON.parse(
        JSON.stringify(this.order)
      );

      let payerState = null;

      if (dc.payers.length) {
        payerState =
          dc.payers[0].intoxication_state;
      }

      if (dc.items.length > 0) {
        const indexesToCopy = [];
        const indexesOrigin = [];

        dc.items.forEach((item) => {
          if (item.status === 1) {
            if (!dc.payers.length) {
              item.count += 1;
            } else if (
              !payerState
              || (
                payerState
                && item.legal_age_class !== 1
              )
            ) {
              item.count += 1;
            }

            indexesOrigin.push(item);
          } else {
            indexesOrigin.push(item);

            if (
              this.findIdenticalItems(
                this.order.items,
                item
              ) === 1
            ) {
              const copyItem =
                JSON.parse(
                  JSON.stringify(item)
                );

              delete copyItem.id;

              copyItem.status = 1;
              copyItem.count = 1;

              indexesToCopy.push(copyItem);
            }
          }
        });

        if (indexesToCopy.length) {
          dc.items = [
            ...indexesOrigin,
            ...indexesToCopy
          ];
        }

        dc.items =
          calculateItemLevelDiscount(dc);

        orderService
          .updateOrder(
            formatDataService
              .formatItemsForAPI(dc.items),
            dc,
          )
          .then((res) => {
            this.handleAPIResponse(
              res.data,
              false,
              true
            );
          })
          .catch((err) => {
            store.dispatch(
              "alert/error",
              err.message
            );

            console.error(err);
          });
      }

      this.$emit("updateOrder", dc);
    },

    findIdenticalItems(itemsArray, item) {
      const identicalItems =
        itemsArray.filter((currentItem) => {
          return (
            currentItem.orderable_id
              === item.orderable_id
            && currentItem.seat
              === item.seat
            && currentItem.modifiers.length
              === item.modifiers.length
          );
        });

      return identicalItems.length;
    },

    handleChangeItemsStatusAfterPrintRunnerChit(
      order
    ) {
      const itemsArray =
        order.items.map((item) => {
          if (item.status == 1) {
            item.status = 2;
          }

          return item;
        });

      order["items"] = itemsArray;

      this.handleAPIResponse(
        order,
        true,
        true
      );
    },

    printMethodsBL(data) {
      const dc = JSON.parse(
        JSON.stringify(this.order)
      );

      const checkPrinter =
        this.$store.state.settings
          .printers
          .find((printer) => {
            return printer.type === 1;
          });

      const hasRunnerChitPrinter =
        this.$store.state.settings
          .printers
          .some((printer) => {
            return printer.type === 2;
          });

      if (
        data.type === "runner-chit"
        && !hasRunnerChitPrinter
      ) {
        this.$refs[
          "confirmationModalRef"
        ].show({
          title: "Printing is not available",
          message:
            "Please set up the printer first",
          okButton: "OK",
        });

        return;
      }

      if (!dc.items.length) {
        return;
      }

      this.orderLoading = true;

      this.emmiter.emit(
        "singleOrderLoading",
        this.orderLoading
      );

      const method =
        data.type === "runner-chit"
          ? printersService.printRunnerChit
          : printersService.printCheck;

      this.waitForPublicCode(dc.id)
        .then(() => {
          if (data.type === "runner-chit") {
            if (checkPrinter?.connection_type == 3) {
              this.doPrintWebRC(dc);
              return;
            }

            if (checkPrinter?.connection_type == 4) {
              this.doPrintWebUsbRC(dc);
              return;
            }

            method(dc.id)
              .then((res) => {
                if (res) {
                  this.handleChangeItemsStatusAfterPrintRunnerChit(
                    dc
                  );

                  this.orderLoading = false;

                  this.emmiter.emit(
                    "singleOrderLoading",
                    this.orderLoading,
                  );
                }
              })
              .catch((err) => {
                this.orderLoading = false;

                this.emmiter.emit(
                  "singleOrderLoading",
                  this.orderLoading
                );

                console.error(err);
              });

            return;
          }

          if (checkPrinter?.connection_type == 3) {
            this.doPrintZPL(dc);
            return;
          }

          if (checkPrinter?.connection_type == 4) {
            this.doPrintWebUsbInvoice(dc);
            return;
          }

          method(dc.id)
            .then((res) => {
              if (res) {
                this.orderLoading = false;

                this.emmiter.emit(
                  "singleOrderLoading",
                  this.orderLoading,
                );
              }
            })
            .catch((err) => {
              this.orderLoading = false;

              this.emmiter.emit(
                "singleOrderLoading",
                this.orderLoading
              );

              console.error(err);
            });
        })
        .catch((err) => {
          console.error(
            "Error printing chit:",
            err.message
          );

          store.dispatch(
            "alert/error",
            "Error printing chit. Please try again later.",
          );
        })
        .finally(() => {
          this.orderLoading = false;

          this.emmiter.emit(
            "singleOrderLoading",
            this.orderLoading
          );
        });
    },

    waitForPublicCode(
      orderId,
      interval,
      maxAttempts
    ) {
      if (!this.isValidOrderId(orderId)) {
        return Promise.reject(
          new Error(
            `waitForPublicCode: invalid orderId (${orderId})`
          )
        );
      }

      const safeInterval =
        Number(
          interval
          ?? window.___env
            ?.orderPublicCodeConfig
            ?.interval
        ) || 700;

      const safeMaxAttemptsRaw =
        maxAttempts
        ?? window.___env
          ?.orderPublicCodeConfig
          ?.maxRetries;

      const safeMaxAttempts =
        Number.isFinite(
          Number(safeMaxAttemptsRaw)
        )
        && Number(safeMaxAttemptsRaw) > 0
          ? Number(safeMaxAttemptsRaw)
          : 12;

      return new Promise(
        (resolve, reject) => {
          let attempts = 0;

          const timer = setInterval(
            async () => {
              attempts += 1;

              try {
                const order =
                  await orderService
                    .getSingleOrder(orderId);

                if (order?.public_code) {
                  clearInterval(timer);
                  resolve(order);

                  return;
                }

                if (
                  attempts
                  >= safeMaxAttempts
                ) {
                  clearInterval(timer);

                  reject(
                    new Error(
                      `public_code is still null after ${attempts} attempts (orderId=${orderId})`
                    )
                  );
                }
              } catch (err) {
                clearInterval(timer);
                reject(err);
              }
            },
            safeInterval
          );
        }
      );
    },

    checkPrintItemSpaces(item) {
      const totalCharacters =
        item.name.length
        + item.count.toString().length
        + this.calcItemTotal(
          item
        ).toString().length;

      let spaces = "";

      const spacesCount =
        27 - totalCharacters;

      for (
        let index = 0;
        index < spacesCount;
        index += 1
      ) {
        spaces += " ";
      }

      return spaces;
    },

    checkPrintSpaces(name, value) {
      const totalCharacters =
        name.length
        + value.toString().length;

      let spaces = "";

      const spacesCount =
        28 - totalCharacters;

      for (
        let index = 0;
        index < spacesCount;
        index += 1
      ) {
        spaces += " ";
      }

      return spaces;
    },

    checkPrintSpacesCenter(name) {
      const totalCharacters =
        name.toString().length;

      let spaces = "";

      const spacesCount =
        (29 - totalCharacters) / 2;

      for (
        let index = 0;
        index < spacesCount;
        index += 1
      ) {
        spaces += " ";
      }

      return spaces;
    },

    doPrintWebRC(order) {
      if (
        this.selected_printer === ""
        && !this.print2default
      ) {
        store.dispatch(
          "alert/error",
          "You must select a printer"
        );

        return;
      }

      if (
        JSPM.JSPrintManager
          .websocket_status
        !== JSPM.WSStatus.Open
      ) {
        store.dispatch(
          "alert/warning",
          "Print service app is not running. Please open JSPrintManager.",
        );

        return;
      }

      const printJob =
        new JSPM.ClientPrintJob();

      printJob.clientPrinter =
        new JSPM.InstalledPrinter(
          this.selected_printer
        );

      printersService
        .getWebPrintRunnerChitFile(
          order.id
        )
        .then((res) => {
          if (
            res.data
            && res.status != 204
          ) {
            printJob.printerCommands =
              res.data;

            printJob.sendToClient();
          }

          printersService
            .printRunnerChit(order.id)
            .then((printResponse) => {
              if (!printResponse) {
                return;
              }

              this.handleChangeItemsStatusAfterPrintRunnerChit(
                order
              );

              this.orderLoading = false;

              this.emmiter.emit(
                "singleOrderLoading",
                this.orderLoading
              );

              if (
                this.$store.getters
                  .logoutOnChitPrint
              ) {
                this.logout();
              }
            })
            .catch((err) => {
              console.error(err);
            });
        })
        .catch((err) => {
          console.error(err);
        });
    },

    async doPrintWebUsbRC(order) {
      try {
        await webusbPrinter.printRunnerChit(order.id);

        printersService
          .printRunnerChit(order.id)
          .then((printResponse) => {
            if (!printResponse) {
              return;
            }

            this.handleChangeItemsStatusAfterPrintRunnerChit(order);
            this.orderLoading = false;
            this.emmiter.emit(
              "singleOrderLoading",
              this.orderLoading
            );

            if (this.$store.getters.logoutOnChitPrint) {
              this.logout();
            }
          })
          .catch((err) => {
            console.error(err);
          });
      } catch (err) {
        this.orderLoading = false;
        this.emmiter.emit(
          "singleOrderLoading",
          this.orderLoading
        );

        store.dispatch(
          "alert/error",
          "Print failed: " + (err.message || err)
        );

        console.error(err);
      }
    },

    async doPrintWebUsbInvoice(order, type, logoutAfterPrint = true) {
      try {
        await webusbPrinter.printInvoice(order.id);

        this.orderLoading = false;
        this.emmiter.emit(
          "singleOrderLoading",
          this.orderLoading
        );
      } catch (err) {
        this.orderLoading = false;
        this.emmiter.emit(
          "singleOrderLoading",
          this.orderLoading
        );

        store.dispatch(
          "alert/error",
          "Print failed: " + (err.message || err)
        );

        console.error(err);

        if (!logoutAfterPrint) {
          throw err;
        }
      } finally {
        this.onModalClose("printCheckModalRef");

        if (type == "close" && logoutAfterPrint) {
          this.logout();
        }
      }
    },

    doPrintZPL(order, type, logoutAfterPrint = true) {
      if (this.selected_printer === "" && !this.print2default) {
        store.dispatch("alert/error", "You must select a printer");

        if (!logoutAfterPrint) {
          throw new Error("Printer is not selected");
        }

        return;
      }

      if (JSPM.JSPrintManager.websocket_status !== JSPM.WSStatus.Open) {
        store.dispatch(
          "alert/warning",
          "Print service app is not running. Please open JSPrintManager."
        );

        if (!logoutAfterPrint) {
          throw new Error("Printer service is not running");
        }

        return;
      }

      const printJob = new JSPM.ClientPrintJob();

      printJob.clientPrinter = new JSPM.InstalledPrinter(
        this.selected_printer
      );

      return printersService
        .getWebPrintInvoiceFile(order.id)
        .then((res) => {
          printJob.printerCommands = res;
          printJob.sendToClient();

          this.orderLoading = false;
          this.emmiter.emit("singleOrderLoading", this.orderLoading);
        })
        .catch((err) => {
          console.error(err);

          if (!logoutAfterPrint) {
            throw err;
          }
        })
        .finally(() => {
          this.onModalClose("printCheckModalRef");

          if (type == "close" && logoutAfterPrint) {
            this.logout();
          }
        });
    },

    async printClosingCheck(id, logoutAfterPrint = true) {
      if (!this.isValidOrderId(id)) {
        console.warn(
          "[SelectedOrder] printClosingCheck called with invalid id:",
          id
        );

        return;
      }

      if (!this.getCheckPrinter) {
        if (logoutAfterPrint) {
          this.logout();
        } else {
          throw new Error("Check printer is not configured");
        }

        return;
      }

      this.orderLoading = true;
      this.emmiter.emit("singleOrderLoading", true);

      try {
        const order = await orderService.getSingleOrder(id);

        const canSkipPublicCodeWait =
          order?.type === "PP"
          || order?.department?.sign_zero_receipt === true;

        if (!canSkipPublicCodeWait) {
          await this.waitForPublicCode(id);
        }
      } catch (error) {
        console.warn(
          "[SelectedOrder] printClosingCheck: public_code wait failed, continue printing",
          error?.message ?? error
        );
      } finally {
        this.orderLoading = false;
        this.emmiter.emit("singleOrderLoading", false);
        this.emmiter.emit("stopLoading");
      }

      const checkPrinter = this.$store.state.settings.printers.find(
        (printer) => printer.type === 1
      );

      if (checkPrinter?.connection_type == 3) {
        const dc = JSON.parse(JSON.stringify(this.order));

        return this.doPrintZPL(dc, "close", logoutAfterPrint);
      }

      if (checkPrinter?.connection_type == 4) {
        const dc = JSON.parse(JSON.stringify(this.order));

        return this.doPrintWebUsbInvoice(
          dc,
          "close",
          logoutAfterPrint
        );
      }

      return printersService
        .printCheck(id)
        .then(() => {
          if (logoutAfterPrint) {
            this.logout();
          }
        })
        .catch((err) => {
          console.error("[SelectedOrder] printCheck failed:", err);

          const message = err?.message === "Network Error"
            ? "Printing service is not available (HTTPS certificate error or network issue)."
            : (
              err?.response?.data?.message
              ?? err?.message
              ?? "Printing failed."
            );

          store.dispatch("alert/error", message);
          throw err;
        });
    },

    shouldStartGuestFlow(data) {
      return Boolean(
        this.$store.state.auth.posData?.enable_guest_signature_flow
        && this.isDefaultOrder
        && data.payment_type === 1
        && (data.payers?.length || this.order.payers?.length)
      );
    },

    cancelGuestHandoff() {
      this.pendingGuestFlowData = null;
    },

    confirmGuestHandoff() {
      if (!this.pendingGuestFlowData) return;

      const data = this.pendingGuestFlowData;
      this.pendingGuestFlowData = null;
      this.startGuestFlow(data);
    },

    startGuestFlow(data) {
      const closeData = {
        ...data,
        payers: data.payers?.length ? data.payers : this.order.payers,
      };

      this.guestFlowData = {
        order: JSON.parse(JSON.stringify(this.order)),
        closeData: JSON.parse(JSON.stringify(closeData)),
      };

      this.$store.dispatch("setGuestFlowActive", true);
      window.history.pushState({ guestFlow: true }, "");
    },

    exitGuestFlow() {
      this.guestFlowData = null;
      this.$store.dispatch("setGuestFlowActive", false);
    },

    async signGuestOrder(signature) {
      const { order, closeData } = this.guestFlowData;

      try {
        order.payers = closeData.payers;
        order.payment_type = closeData.payment_type;

        await orderService.updateOrder(
          formatDataService.formatItemsForAPI(order.items),
          order
        );

        const response = await orderService.signAndCloseOrder({
          id: order.id,
          ...signature,
        });

        const signedOrder = response?.data?.payload?.content ?? response?.data; const closedId = signedOrder?.id ?? order.id;
        let printError = "";

        try {
          await this.printClosingCheck(closedId, false);
        } catch (error) {
          printError =
            "The check is closed, but the receipt could not be printed. Please contact crew.";
        }

        let finalTotal = signedOrder?.total ?? null;

        if (finalTotal === null) {
          try {
            const closedOrder = await orderService.getSingleOrder(closedId);
            finalTotal = closedOrder?.total ?? null;
          } catch (error) {
            console.error(error);
          }
        }

        this.$refs.guestFlowRef?.complete(printError, finalTotal);
      } catch (error) {
        this.$refs.guestFlowRef?.fail(
          error?.response?.data?.message
          ?? "Could not close the check. Please contact crew."
        );
      }
    },

    async skipGuestSignature() {
      if (!this.guestFlowData) return;

      const { order, closeData } = this.guestFlowData;

      try {
        order.payers = closeData.payers;
        order.payment_type = closeData.payment_type;

        await orderService.updateOrder(
          formatDataService.formatItemsForAPI(order.items),
          order
        );

        const response = await orderService.closeOrder(closeData);

        try {
          await this.printClosingCheck(
            response?.data?.id ?? order.id,
            false
          );
        } catch (error) {
          store.dispatch(
            "alert/error",
            "Check closed but receipt printing failed"
          );
        }

        this.finishGuestSignature();
      } catch (error) {
        console.error("[SelectedOrder] skipGuestSignature failed:", error);

        this.$refs.guestFlowRef?.fail(
          error?.response?.data?.message
          ?? "Could not close the check. Please contact crew."
        );
      }
    },

    cancelGuestSignature() {
      this.exitGuestFlow();
      this.logout();
    },

    finishGuestSignature() {
      this.exitGuestFlow();
      this.logout();
    },

    logout() {
      this.$router.replace({
        path: "/auth/login"
      });

      this.$store
        .dispatch("logoutUser")
        .then(() => {
          this.$router.replace({
            path: "/auth/login"
          });
        });
    },

    closePackagePlanOrder(order) {
      orderService
        .closePackagePlanOrder(order)
        .then(async (res) => {
          this.emmiter.emit("closeModal");

          if (
            this.$refs[
              "closeOrderModalRef"
            ]
          ) {
            this.$refs[
              "closeOrderModalRef"
            ].close();
          }

          const closedId =
            res?.data?.id
            ?? res?.data
              ?.payload
              ?.content
              ?.id
            ?? res?.payload
              ?.content
              ?.id;

          if (
            !this.isValidOrderId(closedId)
          ) {
            console.warn(
              "[SelectedOrder] closePackagePlanOrder: invalid closedId",
              {
                closedId,
                res,
              },
            );

            return;
          }

          await this.printClosingCheck(
            closedId
          );

          this.emmiter.emit("stopLoading");

          this.handleAPIResponse(
            order,
            true,
            true,
            "remove"
          );

          if (this.section === "CHECKS") {
            this.refreshOrderDataIntoChecksView();
          }
        })
        .catch((err) => {
          console.error(err);
          this.emmiter.emit("stopLoading");
        });
    },

    closeOrderBL(data) {
      const dc = JSON.parse(
        JSON.stringify(this.order)
      );

      if (data.payers?.length) {
        dc["payers"] = data.payers;
      }

      if (data.payment_type === 2) {
        delete data.payers;

        orderService
          .closeOrder(data)
          .then(async (res) => {
            await this.printClosingCheck(
              res.data.id
            );

            this.emmiter.emit(
              "stopLoading"
            );

            if (res) {
              this.handleAPIResponse(
                dc,
                true,
                true,
                "remove"
              );

              if (
                this.section === "CHECKS"
              ) {
                this.refreshOrderDataIntoChecksView();
              }
            }
          })
          .catch((err) => {
            store.dispatch(
              "alert/error",
              "The given data was invalid"
            );

            console.error(err);

            this.emmiter.emit(
              "stopLoading"
            );
          });

        return;
      }

      orderService
        .updateOrder(
          formatDataService
            .formatItemsForAPI(dc.items),
          dc,
        )
        .then((res) => {
          if (!res) {
            return;
          }

          orderService
            .closeOrder(data)
            .then(async (closeResponse) => {
              await this.printClosingCheck(
                closeResponse.data.id
              );

              this.emmiter.emit(
                "stopLoading"
              );

              this.handleAPIResponse(
                dc,
                true,
                true,
                "remove"
              );

              if (
                this.section === "CHECKS"
              ) {
                this.refreshOrderDataIntoChecksView();
              }
            })
            .catch((err) => {
              console.error(err);

              this.emmiter.emit(
                "stopLoading"
              );
            });
        })
        .catch((err) => {
          store.dispatch(
            "alert/error",
            "The given data was invalid"
          );

          console.error(err);

          this.emmiter.emit(
            "stopLoading"
          );
        });
    },

    changeItemStatusBL(data) {
      const dc = JSON.parse(
        JSON.stringify(this.order)
      );

      const itemIndex =
        dc.items.findIndex((item) => {
          return item.id === data.id;
        });

      if (itemIndex !== -1) {
        dc.items[itemIndex] = data;

        orderService
          .updateOrder(
            formatDataService
              .formatItemsForAPI(dc.items),
            dc,
          )
          .then((res) => {
            this.handleAPIResponse(
              res.data,
              true,
              true
            );
          })
          .catch((err) => {
            store.dispatch(
              "alert/error",
              err.message
            );

            console.error(err);
          });
      }

      this.$emit("updateOrder", dc);
    },

    showDiscountErrorModal(errorText) {
      this.canShowDiscountErrorModal = true;

      this.$nextTick(() => {
        this.$refs[
          "deiscount-error-modal"
        ].openModal(errorText);
      });
    },

    changeItemDiscountBL(data) {
      if (this.order.status === 2) {
        this.orderForReset = {
          ...this.orderForReset
        };

        return;
      }

      const dc = JSON.parse(
        JSON.stringify(this.order)
      );

      const itemIndex =
        dc.items.findIndex((item) => {
          return item.id === data.id;
        });

      if (itemIndex === -1) {
        return;
      }

      dc.items[itemIndex] = data;

      orderService
        .updateOrder(
          formatDataService
            .formatItemsForAPI(dc.items),
          dc,
        )
        .then((res) => {
          this.handleAPIResponse(
            res.data,
            true
          );
        })
        .catch((err) => {
          this.showDiscountErrorModal(
            err?.response
              ?.data
              ?.message
          );

          this.getCurrentOrderWithoutRefresh();
        });
    },

    async changeOrderDiscountBL(data) {
      const selectedOrder = JSON.parse(
        JSON.stringify(data)
      );

      if (this.order.status === 2) {
        this.orderForReset =
          selectedOrder;

        this.addAdjustReason(
          "Discount changed"
        );

        try {
          const { data: recalculatedOrder } =
            await orderService
              .recalculateOrder(
                this.orderForReset
              );

          this.handleRecalculateAPIResponse(
            recalculatedOrder,
            true
          );
        } catch (error) {
          const resetOrder = JSON.parse(
            JSON.stringify(
              this.orderForCancelReset
            ),
          );

          resetOrder.gratuity_percent =
            this.order.gratuity_percent;

          resetOrder.gratuity_amount =
            this.order.gratuity_amount;

          resetOrder.gratuity_type =
            this.order.gratuity_type;

          this.order = resetOrder;
        }

        return;
      }

      try {
        const { data: updatedOrder } =
          await orderService.updateOrder(
            formatDataService
              .formatItemsForAPI(
                selectedOrder.items
              ),
            selectedOrder,
          );

        this.handleAPIResponse(
          updatedOrder,
          true
        );
      } catch (error) {
        this.showDiscountErrorModal(
          error?.response
            ?.data
            ?.message
        );

        this.getCurrentOrderWithoutRefresh();
      }
    },

    async changeGratuityForDefaultOrder(
      data
    ) {
      const selectedOrder =
        JSON.parse(
          JSON.stringify(this.order)
        );

      if (this.order.status === 2) {
        const orderForReset = {
          ...selectedOrder,
          gratuity_type:
            data.gratuity_type === "amount"
              ? constants.gratuityTypes.amount
              : constants.gratuityTypes
                .percentage,
        };

        switch (
          orderForReset.gratuity_type
        ) {
          case constants
            .gratuityTypes
            .amount:
            orderForReset[
              "gratuity_amount"
            ] = data.gratuity_amount;

            delete orderForReset[
              "gratuity_percent"
            ];

            break;

          case constants
            .gratuityTypes
            .percentage:
            orderForReset[
              "gratuity_percent"
            ] = data.gratuity_percent;

            delete orderForReset[
              "gratuity_amount"
            ];

            break;
        }

        this.orderForReset =
          Object.assign(
            this.order,
            orderForReset
          );

        this.addAdjustReason(
          "Add Additional tip"
        );

        try {
          const { data: recalculatedOrder } =
            await orderService
              .recalculateOrder(
                this.orderForReset
              );

          this.handleRecalculateAPIResponse(
            recalculatedOrder,
            true
          );
        } catch (error) {
          console.error(error);
        }

        return;
      }

      try {
        const { data: updatedOrder } =
          await orderService.updateOrder(
            formatDataService
              .formatItemsForAPI(
                selectedOrder.items
              ),
            selectedOrder,
            false,
            data,
          );

        this.handleAPIResponse(
          updatedOrder,
          true
        );
      } catch (error) {
        console.error(error);
      }
    },

    async changeGratuityForPackagePlanOrder(
      data
    ) {
      const selectedOrder =
        JSON.parse(
          JSON.stringify(this.order)
        );

      if (this.order.status === 2) {
        const orderForReset = {
          ...selectedOrder,
          ...data,
        };

        orderForReset.gratuity_type =
          data.gratuity_type === "amount"
            ? constants.gratuityTypes.amount
            : constants.gratuityTypes
              .percentage;

        this.orderForReset =
          Object.assign(
            this.order,
            orderForReset
          );

        this.addAdjustReason(
          "Add Additional tip"
        );

        try {
          let payload = {
            ...data,

            reason:
              this.orderResetReasons
                ?.filter(
                  (
                    value,
                    index,
                    array
                  ) => {
                    return (
                      array.indexOf(value)
                      === index
                    );
                  }
                )
                ?.join("; "),
          };

          payload.gratuity_type =
            data.gratuity_type === "amount"
              ? constants.gratuityTypes.amount
              : constants.gratuityTypes
                .percentage;

          const { data: recalculatedData } =
            await packagePlansService
              .recalculateOrder(
                this.orderForReset.id,
                payload,
              );

          this.handleRecalculateAPIResponse(
            recalculatedData
              ?.payload
              ?.content,
            true,
          );
        } catch (error) {
          console.error(error);
        }

        return;
      }

      const packagePlanIds = [];

      selectedOrder
        ?.items
        ?.forEach((item) => {
          if (
            !item?.package_plan_id
            || packagePlanIds.includes(
              item.package_plan_id
            )
          ) {
            return;
          }

          packagePlanIds.push(
            item.package_plan_id
          );
        });

      let payload = {
        crew_id:
          selectedOrder
            ?.serviced_by
            ?.id,

        package_plans: packagePlanIds,

        payers:
          selectedOrder
            ?.payers
            ?.map((payer) => {
              return {
                user_profile_id: payer.id,
                addition_mode_id:
                  payer.addition_mode?.id,
              };
            }),

        ...data,
      };

      payload =
        formatDataService
          .addGratuityToPayload(
            payload,
            data
          );

      try {
        const { data: updatedOrder } =
          await packagePlansService
            .updatePackagePlanOrder(
              payload,
              selectedOrder.id,
            );

        this.handleAPIResponse(
          updatedOrder.payload.content,
          true
        );
      } catch (error) {
        console.error(error);
      }
    },

    async changeGratuityBL(data) {
      if (this.isPackagePlanOrder) {
        await this
          .changeGratuityForPackagePlanOrder(
            data
          );

        return;
      }

      await this
        .changeGratuityForDefaultOrder(
          data
        );
    },

    addAdjustReason(reason) {
      this.orderResetReasons.push(reason);
    },

    changeOrderTableBL(data) {
      const dc = JSON.parse(
        JSON.stringify(this.order)
      );

      const previousTempId = dc.tempId;

      dc.table = parseInt(data.table);
      dc.seat = data.seat;

      this.$forceUpdate();

      if (dc.id === constants.tempOrderID) {
        const tempOrders =
          JSON.parse(
            localStorage.getItem(
              "tempOrders"
            )
          ) ?? [];

        const tempOrderIndex =
          tempOrders.findIndex(
            (tempOrder) => {
              return (
                tempOrder.tempId
                === previousTempId
              );
            }
          );

        if (tempOrderIndex === -1) {
          return;
        }

        tempOrders[tempOrderIndex] = dc;

        localStorage.setItem(
          "tempOrders",
          JSON.stringify(tempOrders),
        );

        this.handleAPIResponse(
          dc,
          true,
          true
        );

        return;
      }

      orderService
        .updateOrder(
          formatDataService
            .formatItemsForAPI(dc.items),
          dc,
          true,
        )
        .then((res) => {
          if (res) {
            this.handleAPIResponse(
              res.data,
              true,
              true
            );
          }
        })
        .catch((err) => {
          store.dispatch(
            "alert/error",
            err.message
          );

          console.error(err);
        });
    },

    openCommentModal() {
      if (!this.getBusinessDay.active) {
        store.dispatch(
          "alert/error",
          "Business day is not in operation yet",
        );

        return;
      }

      this.openModal("commentModalRef");
    },

    reopenOrderBL(text) {
      const data = {
        id: this.order.id,
        reason: text,
      };

      orderService
        .reopenOrder(data)
        .then((res) => {
          this.handleAPIResponse(
            res.data,
            true
          );
        })
        .catch((err) => {
          console.error(err);
        });
    },

    async resetOrderBL() {
      const cantAdjustOrder = Boolean(
        !this.orderForReset
        || !this.orderResetReasons?.length
      );

      if (cantAdjustOrder) {
        return;
      }

      const reason =
        this.orderResetReasons
          ?.filter(
            (value, index, array) => {
              return (
                array.indexOf(value)
                === index
              );
            }
          )
          ?.join("; ");

      const preparePayload = (order) => {
        if (this.isDefaultOrder) {
          return order;
        }

        const payload = {
          reason: reason,
        };

        return formatDataService
          .addGratuityToPayload(
            payload,
            this.orderForReset,
          );
      };

      if (this.isDefaultOrder) {
        const { data: adjustedOrder } =
          await orderService.adjustOrder(
            preparePayload(
              this.orderForReset
            ),
            reason,
          );

        if (
          adjustedOrder.id
          !== this.order.id
        ) {
          await this.$router.push({
            name: "CheckDetails",

            params: {
              id: adjustedOrder.id,
            },
          });
        }

        this.handleAPIResponse(
          adjustedOrder,
          true,
          "replace",
          "replace",
        );

        return;
      }

      const { data: adjustedOrder } =
        await packagePlansService
          .adjustOrder(
            this.orderForReset.id,
            preparePayload(
              this.orderForReset
            ),
          );

      if (
        adjustedOrder
          .payload
          ?.content
          ?.id
        !== this.order.id
      ) {
        await this.$router.push({
          name: "CheckDetails",

          params: {
            id:
              adjustedOrder
                .payload
                ?.content
                ?.id,
          },
        });
      }

      this.handleAPIResponse(
        adjustedOrder
          .payload
          ?.content,
        true,
        "replace",
        "replace",
      );
    },

    cancelResetingOrder() {
      this.order = JSON.parse(
        JSON.stringify(
          this.orderForCancelReset
        ),
      );

      this.orderForReset = null;
    },

    refreshOrderDataIntoChecksView() {
      this.getOrder(
        this.$route.params.id
      );

      this.toggleOrderDetails(true);
    },
  },

  watch: {
    "$route.params.id"(nextId) {
      if (this.section !== "CHECKS") {
        return;
      }

      if (!this.isValidOrderId(nextId)) {
        return;
      }

      if (
        this.order?.id === Number(nextId) ||
        this.order?.id === nextId
      ) {
        return;
      }

      this.getOrder(nextId);
      this.toggleOrderDetails(true);
    },

    tempOrder: {
      handler: function (data) {
        if (!data) {
          this.order = null;
          this.toggleOrderDetails(false);

          return;
        }

        if (!this.isValidOrderId(data.id)) {
          console.warn(
            "[SelectedOrder] tempOrder has invalid id:",
            data.id,
            data,
          );

          console.trace(
            "[SelectedOrder] tempOrder watcher call stack",
          );

          return;
        }

        if (data.id === constants.tempOrderID) {
          this.order = this.tempOrder;

          return;
        }

        if (
          (this.order && data.id !== this.order.id) ||
          !this.order
        ) {
          this.getOrder(data.id);

          if (this.$refs["seat-dd"]) {
            this.filterBySeats = "All";
            this.$refs["seat-dd"].refresh();
          }
        }
      },

      deep: true,
    },
  },
};
</script>

<style lang="less" scoped>
#selected-order {
  border-radius: 10px;
  box-shadow: 0 1px 3px -1px #000000;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  flex-direction: column;

  .no-order-selected {
    font-weight: 400;
    font-size: 24px;
    line-height: 32px;
    text-transform: uppercase;
  }

  .order {
    width: 100%;
    height: 100%;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-y;
    overscroll-behavior-y: contain;
    display: flex;
    justify-content: flex-start;
    align-items: stretch;
    flex-wrap: nowrap;
    flex-direction: column;

    .order-details {
      width: 100%;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      flex-wrap: wrap;
      flex: 0 0 auto;
      box-sizing: border-box;
      padding: 10px;

      .col {
        width: 25%;

        &:first-child {
          width: 50%;
          margin-right: 3%;
          margin-bottom: 10px;
        }

        &:nth-child(2) {
          width: 22%;
          margin-right: 3%;
          margin-bottom: 10px;
        }

        &:nth-child(3) {
          width: 22%;
          margin-right: 0;
          margin-bottom: 10px;
        }

        &:nth-child(4) {
          margin-right: 3%;
          width: 50%;
        }

        &:nth-child(5) {
          width: 22%;
        }

        .title {
          color: #1c282d;
          font-size: 10px;
          font-weight: 600;
          text-align: left;
        }

        :deep(.custom-select) {
          width: auto;
          height: auto;
          line-height: 30px;

          .selected {
            border-color: lightgray !important;

            &.no-items {
              .selected-output {
                font-size: 10px;
              }
            }

            .selected-output {
              font-size: 18px;
              color: #1c282d;
              font-weight: 400;
            }
          }

          .items {
            border-color: lightgray !important;

            .item {
              .text {
                font-size: 18px;
                color: #1c282d;
                font-weight: 400;
              }
            }
          }

          input {
            width: 100%;
            height: auto;
            box-sizing: border-box;
          }
        }

        .col-data {
          position: relative;
          z-index: 2;
          border-radius: 4px;
          background: #ffffff;
          border: 1px solid lightgray;
          box-sizing: border-box;
          padding-left: 11px;
          cursor: pointer;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
          width: auto;
          height: auto;
          line-height: 30px;
          padding-right: 20px;
          display: flex;

          span {
            font-size: 18px;
            color: #1c282d;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .dropdown-icon {
            position: absolute;
            right: 7px;
            width: 7px;
            height: 7px;
            top: 50%;
            margin-top: -3.5px;
          }

          &.disabled {
            padding-right: 0px;
            background-color: transparent;

            &:after {
              content: "";
            }
          }

          &.not-specified {
            span {
              font-size: 10px;
            }
          }
        }

        &_more {
          height: 46px;
          display: flex;
          align-items: flex-end;
          justify-content: flex-end;

          .more_btn {
            width: 99px;
            height: 35px;
          }
        }
      }

      &.retail-mode {
        .col {
          &:first-child {
            width: 100%;
          }

          &:nth-child(2) {
            width: 50%;
          }

          &:nth-child(3),
          &:nth-child(4),
          &:nth-child(5) {
            width: 22%;
          }

          .title {
            color: #1c282d;
            font-size: 10px;
            font-weight: 600;
            text-align: left;
          }

          :deep(.custom-select) {
            width: auto;
            height: auto;
            line-height: 30px;

            .selected {
              border-color: lightgray !important;

              &.no-items {
                .selected-output {
                  font-size: 10px;
                }
              }

              .selected-output {
                font-size: 18px;
                color: #1c282d;
                font-weight: 400;
              }
            }

            .items {
              border-color: lightgray !important;

              .item {
                .text {
                  font-size: 18px;
                  color: #1c282d;
                  font-weight: 400;
                }
              }
            }

            input {
              width: 100%;
              height: auto;
              box-sizing: border-box;
            }
          }

          .col-data {
            position: relative;
            z-index: 2;
            border-radius: 4px;
            background: #ffffff;
            border: 1px solid lightgray;
            box-sizing: border-box;
            padding-left: 11px;
            cursor: pointer;
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
            user-select: none;
            width: auto;
            height: auto;
            line-height: 30px;
            padding-right: 20px;
            display: flex;

            span {
              font-size: 18px;
              color: #1c282d;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }

            .dropdown-icon {
              position: absolute;
              right: 7px;
              width: 7px;
              height: 7px;
              top: 50%;
              margin-top: -3.5px;
            }

            &.disabled {
              padding-right: 0px;
              background-color: transparent;

              &:after {
                content: "";
              }
            }

            &.not-specified {
              span {
                font-size: 10px;
              }
            }
          }

          &_more {
            height: 46px;
            display: flex;
            align-items: flex-end;
            justify-content: flex-end;

            .more_btn {
              width: 99px;
              height: 35px;
            }
          }
        }
      }
    }

    .items-list {
      width: 100%;
      flex: 0 0 auto;
      min-height: 120px;
      overflow: visible;
      background-color: #ffffff;
      position: relative;

      table {
        width: 100%;
        table-layout: fixed;
        margin: 0;
        box-sizing: border-box;
        position: relative;
        border-collapse: separate;

        thead {
          position: sticky;
          top: -1px;
          background-color: #ffffff;
          z-index: 2;
        }

        &.package-plan__table {
          td {
            height: 40px;
            box-sizing: border-box;
          }

          .package-plan__th-item {
            width: 45% !important;
          }

          .package-plan__th-payer {
            width: 40% !important;
          }

          .package-plan__th-price {
            width: 15% !important;
          }
        }

        tr {
          &:nth-child(odd) {
            background-color: #faf9fb;
          }

          th,
          td {
            &:nth-child(4) {
              position: relative;
            }
          }
        }

        th {
          border-top: 2px solid #e9ecef;
          border-bottom: 2px solid #e9ecef;
          vertical-align: middle;
          height: 30px;
          padding: 5px 10px;
          overflow-wrap: break-word;
          color: #14191f;
          font-size: 10px;
          font-weight: 700;
          text-align: center;
          text-transform: uppercase;

          &.left {
            text-align: left;
          }

          &:nth-child(1) {
            width: 25% !important;
            text-align: center;
            cursor: pointer;
          }

          &:nth-child(2) {
            width: 10% !important;
          }

          &:nth-child(3) {
            width: 15% !important;
          }

          &:nth-child(4) {
            width: 10% !important;
          }

          &:nth-child(5) {
            width: 10% !important;
          }

          &:nth-child(6) {
            width: 12% !important;
          }

          &:nth-child(7) {
            width: 10% !important;
          }

          &:nth-child(8) {
            width: 7% !important;
          }

          &:nth-child(9) {
            width: 8.5% !important;
          }
        }

        td {
          vertical-align: middle;
          height: auto;
          padding: 4px 2px;
          overflow-wrap: break-word;
          color: #1a141f;
          font-size: 14px;
          text-align: center;

          &:first-child {
            span {
              padding-left: 4px;
              text-align: left;
              cursor: pointer;
              overflow: hidden;
              text-overflow: ellipsis;
              display: -webkit-box;
              -webkit-line-clamp: 3;
              line-clamp: 3;
              -webkit-box-orient: vertical;

              &.condiment-name {
                padding-left: 20px;
              }
            }
          }

          .state {
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;

            .status {
              background-color: red;

              &.status-1 {
                background-color: #eb262b;
              }

              &.status-2 {
                background-color: #ffcb05;
              }

              &.status-3 {
                background-color: #82b941;
              }
            }

            .circle {
              height: 12px;
              width: 12px;
              border-radius: 100%;
            }

            .text {
              font-weight: normal;
              font-size: 12px;
              line-height: 148%;
              align-items: center;
              color: #1a141f;
              margin-left: 8px;
            }
          }

          &:nth-child(3) {
            padding: 5px 0;
          }

          &.item-discount {
            span {
              border: 1px solid #d9e8f3;
              border-radius: 8px;
              padding: 4px;
              display: flex;
              text-align: right;
              justify-content: flex-end;
              align-items: center;
              width: 70%;
              margin: 0 0 0 auto;
            }

            &.condiment-discount {
              span {
                border: none;
              }
            }
          }

          &.qty-td {
            div {
              display: flex;
              justify-content: space-between;
              align-items: center;
              flex-wrap: nowrap;
              margin: 0 auto;
              width: 90%;

              img {
                cursor: pointer;
              }
            }
          }

          &.del {
            text-align: center;

            button {
              width: 36px;
              height: 32px;
              background: #ffffff;
              border: 1px solid #d9e8f3;
              border-radius: 8px;
              cursor: pointer;
              display: flex;
              justify-content: center;
              align-items: center;
              margin: 0 auto;

              img {
                width: 14px;
                height: 18px;
              }
            }
          }
        }
      }

      &::-webkit-scrollbar {
        display: none;
      }
    }

    .order-calculations {
      width: 100%;
      flex: 0 0 auto;
      box-sizing: border-box;
      padding: 0;
      border-radius: 8px;
      background-color: #fff;
      border-top: 1px solid #e5e0eb;

      .calc-wrapper {
        width: 100%;
        position: relative;

        .row {
          width: 77%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: nowrap;
          min-height: 30px;

          &:first-child {
            .right-column {
              border: none;
            }
          }

          .left-column {
            width: 43%;
            height: 36px;
            display: flex;
            justify-content: center;
            align-items: center;
            flex-wrap: nowrap;
          }

          .right-column {
            width: 57%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: nowrap;
            border-top: 1px solid #e3e3e3;
            height: 36px;
          }

          .switch {
            .slider {
              position: absolute;
              cursor: pointer;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background-color: #cccccc;
              box-shadow: 0 0 1px #3f8ec5;
              border-radius: 34px;
              font-weight: 600;
              font-size: 12px;
              display: flex;
              align-items: center;
              text-transform: uppercase;
              justify-content: flex-end;
              padding-right: 10px;
              -webkit-transition: 0.5s;
              transition: 0.5s;

              &.active {
                color: #ffffff !important;
                justify-content: flex-start !important;
                padding-left: 10px;
                background: #3f8ec5;

                &:before {
                  -webkit-transform: translateX(72px);
                  -ms-transform: translateX(72px);
                  transform: translateX(72px);
                }
              }

              &:before {
                position: absolute;
                content: "";
                height: 19px;
                width: 19px;
                left: 4px;
                bottom: 4px;
                border-radius: 50%;
                background-color: white;
                -webkit-transition: 0.4s;
                transition: 0.4s;
              }
            }
          }

          .title {
            text-align: left;
            font-style: normal;
            font-weight: 600;
            font-size: 12px;
            text-transform: uppercase;
            color: #1c282d;
          }

          .amount {
            font-style: normal;
            font-weight: 600;
            font-size: 12px;
            text-transform: uppercase;
            color: #1c282d;
            text-align: right;
          }

          &.total {
            .right-column {
              border-top-width: 2px;

              .title,
              .amount {
                font-weight: 700;
              }
            }
          }
        }
      }
    }

    .controls {
      width: calc(100% - 20px);
      display: flex;
      justify-content: center;
      align-items: center;
      flex-wrap: wrap;
      padding: 10px;
      box-sizing: border-box;
      flex: 0 0 auto;

      .row {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: nowrap;

        &:not(:last-child) {
          margin-bottom: 10px;
        }

        &.x3 {
          button {
            width: 32%;
            height: 36px;
          }
        }

        &.x2 {
          button {
            width: 49%;
          }
        }

        &.x1 {
          button {
            width: 100%;
          }
        }

        &.auto {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 11px;

          button {
            height: 36px;
            min-width: 32%;
            max-width: 50%;
            width: 100%;
          }
        }
      }
    }
  }

  .def-btn {
    padding: 10px 2px;

    &.full-width {
      width: 100%;
    }
  }
}
@media (max-width: 1200px) {
  #selected-order {
    min-width: 0;

    .order {
      .items-list {
        table {
          th {
            height: 30px;
            padding: 0 6px;
            font-size: 7px;
          }

          td {
            height: 30px;
            padding: 0 6px;
            font-size: 12px;

            &.qty-td {
              padding: 0;
            }
          }
        }
      }

      .order-calculations {
        .calc-wrapper {
          .row {
            .switch {
              width: 80px;

              .slider {
                padding-right: 4px;
                font-size: 8px;

                &.active {
                  padding-left: 7px;

                  &:before {
                    transform: translateX(55px);
                  }
                }

                &:before {
                  left: 3px;
                }
              }
            }

            .discount-details {
              font-size: 10px;
            }

            div {
              font-size: 12px;
            }
          }
        }
      }

      .controls {
        button {
          height: 40px;
          padding: 5px 10px;
          font-size: 13px;
        }
      }
    }
  }
}

@media (max-width: 1100px) {
  #selected-order .order .items-list table:not(.package-plan__table) {
    th {
      padding: 0 3px;
      font-size: 10px;

      &:nth-child(1) { width: 25% !important; }
      &:nth-child(2) { width: 9% !important; }
      &:nth-child(3) { width: 14% !important; }
      &:nth-child(4) { width: 10% !important; }
      &:nth-child(5) { width: 9% !important; }
      &:nth-child(6) { width: 11% !important; }
      &:nth-child(7) { width: 8% !important; }
      &:nth-child(8) { width: 6% !important; }
      &:nth-child(9) { width: 8% !important; }
    }

    td {
      padding: 4px 3px;
      font-size: 13px;
    }
  }
}

.loading {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

#check-details-wrapper {
  position: fixed;
  left: 0;
  top: 84px;
  padding-left: 8px;
  width: 100%;
  height: calc(var(--app-height, 100vh) - 84px);
  background-color: #ecf4f9;
  z-index: 999;

  img {
    position: absolute;
    top: 14px;
    right: 14px;
    width: 28px;
    cursor: pointer;
  }
}
@media (max-height: 900px) {
  #selected-order {
    .order {
      .order-details {
        padding: 6px;

        .col {
          &:first-child,
          &:nth-child(2),
          &:nth-child(3) {
            margin-bottom: 6px;
          }

          :deep(.custom-select) {
            line-height: 24px;

            .selected .selected-output,
            .items .item .text {
              font-size: 14px;
            }
          }

          .col-data {
            line-height: 24px;

            span {
              font-size: 14px;
            }
          }

          &_more {
            height: 35px;

            .more_btn {
              height: 28px;
            }
          }
        }
      }

      .order-calculations {
        .calc-wrapper {
          .row {
            min-height: 24px;

            .left-column,
            .right-column {
              height: 28px;
            }

            .title,
            .amount {
              font-size: 10px;
            }
          }
        }
      }

      .controls {
        padding: 6px;

        .row {
          &:not(:last-child) {
            margin-bottom: 6px;
          }

          button {
            height: 36px;
            padding: 5px 2px;
            font-size: 13px;
          }

          &.x3 button,
          &.auto button {
            height: 32px;
          }
        }
      }
    }
  }
}
</style>